/**
 * Panneau RDP — bureau Windows natif d'un host via IronRDP compilé en WASM.
 *
 * ── Principe ─────────────────────────────────────────────────────────────────
 * Contrairement au panneau `video.tsx` (capture d'écran H.264 par le helper),
 * ce panneau parle le *vrai* protocole RDP. Un client RDP — build WebAssembly
 * du crate officiel `ironrdp-web` de Devolutions, vendoré sous
 * `src/operator/vendor/ironrdp/` — tourne dans le navigateur et rend le bureau
 * distant dans un `<canvas>`.
 *
 * ── Mode DIRECT (« plain ») ───────────────────────────────────────────────────
 * Le build `ironrdp-web` standard parle le protocole gateway RDCleanPath de
 * Devolutions : c'est un *proxy* qui réalise la négociation X.224 et le
 * handshake TLS. Le tunnel RDP de RemoteWS, lui, transporte le RDP *brut*
 * (l'Agent C# ouvre `TcpClient("127.0.0.1",3389)` et pompe les octets tels
 * quels dans la WS) — il n'y a pas de proxy RDCleanPath.
 *
 * Le crate `ironrdp-web` a donc été patché (cf. `crates/ironrdp-web/src/tls.rs`
 * et la branche `connect_direct` de `session.rs`) pour ajouter un chemin de
 * connexion DIRECT : le client WASM déroule lui-même toute la séquence RDP —
 * X.224, **handshake TLS en-WASM via rustls/ring**, puis CredSSP + reste — au
 * dessus d'un simple tuyau d'octets bruts. Ce mode est activé en passant
 * l'extension `use_rdcleanpath = false` au `SessionBuilder`.
 *
 * ── Transport ────────────────────────────────────────────────────────────────
 * IronRDP-web ouvre normalement une WebSocket *directe* vers un proxy RDP.
 * Ici le transport doit passer par la WebSocket relais déjà multiplexée de la
 * console. On utilise donc le shim `RdpTunnelSocket` (cf. `lib/rdp-transport.ts`)
 * qui se substitue temporairement au `WebSocket` global : le WASM croit ouvrir
 * une WS, mais ses octets RDP sont en réalité encapsulés dans des trames
 * WebSocket binaires du relais, et les octets reçus de l'agent lui sont
 * réinjectés.
 *
 * Protocole relais (fixé, miroir de l'implémentation host/relais) :
 *   - `rdp:open`  {tunnelId,targetHost,targetPort} — ouvre le tunnel (JSON).
 *   - (données)   trame binaire [0x52,0x44][tunnelId:16o][payload RDP brut]
 *                                               — bidirectionnel, sans base64.
 *   - `rdp:close` {tunnelId, reason?}           — fermeture (JSON).
 *   - `rdp:error` {tunnelId, message}           — échec signalé par l'agent (JSON).
 *
 * ── Sécurité ─────────────────────────────────────────────────────────────────
 * Les credentials Windows (domaine/utilisateur/mot de passe) sont saisis dans
 * ce panneau et transmis UNIQUEMENT au client IronRDP local (en mémoire). Ils
 * ne transitent jamais en clair vers le relais et ne sont jamais journalisés.
 * Les octets RDP (chiffrés par RDP lui-même) ne polluent pas non plus les logs.
 */
import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount, type Component } from "solid-js";
import { useWs } from "@/shared/ws";
import { apiFetch } from "@/shared/api-fetch";
import { CredentialInput, type CredentialInputResult } from "@/shared/credential-input";
import { useConfirm } from "@/shared/confirm-dialog";
import { wsUrlList } from "@/shared/native";
import { refreshRuntimeConfig } from "@/shared/runtime-config";
import { SC as ScCodes } from "@/shared/keymap";
import { resolveKey } from "@/shared/keyboard-profile";
import { KeyboardProfileEditor } from "@/shared/keyboard-profile-editor";
import { useKeyboardProfile } from "@/shared/use-keyboard-profile";
import {
  createClipboardEngine,
  entryFromSnapshot,
  type ClipboardSnapshot,
  type ClipboardTransport,
} from "@/shared/clipboard-engine";
import { ClipboardMenu } from "@/shared/clipboard-panel";
import {
  MUX_DEFAULT_MAX_PAYLOAD_BYTES,
  MuxChannel,
  MuxRoutingScope,
  decodeMuxFrame,
  decodeMuxJsonPayload,
  encodeMuxFrame,
  encodeMuxJsonFrame,
  isMuxBrowserEnabled,
  isMuxFrame,
  muxBrowserReadyChannels,
  rdpStreamIdFromFrame,
  rdpStreamIdFromTunnelId,
} from "@/shared/mux";
import { registerPanel } from "@/panel-registry";
import {
  clampRdpDesktopSize,
  clampRdpToolbarPosition,
  clipboardAllowsLocalToRemote,
  clipboardAllowsRemoteToLocal,
  defaultCmdKeyModeForPlatform,
  isLocalPasteShortcut,
  resolveRemotePasteDelay,
  rdpDesktopSizeForSurface,
  type RdpCmdKeyMode,
  type RdpClipboardMode,
  type RdpQualityProfile,
  type RdpToolbarMode,
  type RdpToolbarPosition,
} from "./lib/rdp-ui";
import { installTunnelSocket, parseRdpFrame, type RdpTunnelSocket } from "./lib/rdp-transport";
import "./rdp.css";

// ── Typage minimal de l'API IronRDP-web (cf. ironrdp-wasm/pkg/rdp_client.d.ts) ─
// On ne dépend pas statiquement du package : il est chargé en import dynamique.
// Ces interfaces décrivent juste la surface réellement utilisée ici.

interface IronDesktopSize {
  readonly width: number;
  readonly height: number;
}

interface IronDeviceEvent {
  readonly __brand: "DeviceEvent";
}

interface IronInputTransaction {
  addEvent(event: IronDeviceEvent): void;
}

interface IronSession {
  desktopSize(): IronDesktopSize;
  applyInputs(tx: IronInputTransaction): void;
  invokeExtension(ext: unknown): unknown;
  onClipboardPaste(content: IronClipboardData): Promise<void>;
  releaseAllInputs(): void;
  resize(
    width: number,
    height: number,
    scaleFactor?: number | null,
    physicalWidth?: number | null,
    physicalHeight?: number | null,
  ): void;
  run(): Promise<{ reason?: () => string }>;
  shutdown(): void;
  // Synchronise l'état des touches de verrouillage (Caps/Num/Scroll/Kana) entre
  // l'opérateur et l'hôte. Sans cet appel après connexion, l'état LED peut être
  // désynchronisé → majuscules inversées jusqu'à un double-appui sur CapsLock.
  synchronizeLockKeys(scrollLock: boolean, numLock: boolean, capsLock: boolean, kanaLock: boolean): void;
}

interface IronSessionBuilder {
  username(v: string): IronSessionBuilder;
  password(v: string): IronSessionBuilder;
  serverDomain(v: string): IronSessionBuilder;
  destination(v: string): IronSessionBuilder;
  proxyAddress(v: string): IronSessionBuilder;
  authToken(v: string): IronSessionBuilder;
  desktopSize(v: IronDesktopSize): IronSessionBuilder;
  renderCanvas(c: HTMLCanvasElement): IronSessionBuilder;
  extension(e: unknown): IronSessionBuilder;
  forceClipboardUpdateCallback(cb: () => IronClipboardData | Promise<IronClipboardData>): IronSessionBuilder;
  remoteClipboardChangedCallback(cb: (content: IronClipboardData) => void | Promise<void>): IronSessionBuilder;
  setCursorStyleCallback(cb: (style: string) => void): IronSessionBuilder;
  setCursorStyleCallbackContext(ctx: unknown): IronSessionBuilder;
  connect(): Promise<IronSession>;
}

interface IronClipboardItem {
  mimeType(): string;
  value(): unknown;
}

interface IronClipboardData {
  addBinary(mimeType: string, binary: Uint8Array): void;
  addText(mimeType: string, text: string): void;
  isEmpty(): boolean;
  items(): IronClipboardItem[];
}

/** Surface du module `ironrdp-wasm` chargé dynamiquement. */
interface IronRdpModule {
  default: (input?: unknown) => Promise<unknown>; // __wbg_init
  setup: (logLevel: string) => void;
  SessionBuilder: new () => IronSessionBuilder;
  DesktopSize: new (w: number, h: number) => IronDesktopSize;
  InputTransaction: new () => IronInputTransaction;
  DeviceEvent: {
    keyPressed(scancode: number): IronDeviceEvent;
    keyReleased(scancode: number): IronDeviceEvent;
    mouseButtonPressed(button: number): IronDeviceEvent;
    mouseButtonReleased(button: number): IronDeviceEvent;
    mouseMove(x: number, y: number): IronDeviceEvent;
    wheelRotations(vertical: boolean, amount: number, unit: number): IronDeviceEvent;
    unicodePressed(unicode: string): IronDeviceEvent;
    unicodeReleased(unicode: string): IronDeviceEvent;
  };
  Extension: new (ident: string, value: unknown) => unknown;
  ClipboardData: new () => IronClipboardData;
}

// Table de scancodes PS/2 Set 1 : déplacée vers `@/shared/keymap`
// (CODE_TO_SCANCODE) — partagée avec le moteur de profils clavier. Ce panneau
// consomme désormais les scancodes via `resolveKey(keyboardProfile(), e)`.

// Marqueur d'URL reconnu par le shim pour intercepter la WebSocket du WASM.
const PROXY_MARKER = "rdp-tunnel://relay";

type RdpStatus = "idle" | "loading" | "connecting" | "connected" | "error";
type RdpJsonMessage = { type?: string; [key: string]: unknown };
type RdpJsonHandler = (msg: RdpJsonMessage) => void;
type RdpBinaryHandler = (data: ArrayBuffer) => void;
// Bornes RDP Display Control (MS-RDPEDISP §2.2.2.2.2). Le serveur rejette
// silencieusement toute demande hors de [200,8192] ou de largeur impaire.
const RDP_MIN_WIDTH = 1024; // au-dessus du plancher proto (200) pour rester lisible
const RDP_MIN_HEIGHT = 768;
const RDP_MAX_WIDTH = 3840; // sous le plafond proto (8192) : couvre le 4K UHD
const RDP_MAX_HEIGHT = 2160;
const DEFAULT_RDP_PORT = 3389;
const TOOLBAR_MARGIN = 10;
const TOOLBAR_DEFAULT_POSITION: RdpToolbarPosition = { x: 12, y: 12 };
// Scancodes PS/2 pour la synthèse du Ctrl+V (cf. @/shared/keymap `SC`).
const RDP_CTRL_SCANCODE = ScCodes.CTRL;
const RDP_V_SCANCODE = ScCodes.V;
const RDP_RESIZE_DEBOUNCE_MS = 180;

function clampRdpSize(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function splitRdpHostPort(rawHost: string | undefined, rawPort?: string | number): { host: string; port: string } {
  const fallbackPort = String(rawPort ?? DEFAULT_RDP_PORT).trim() || String(DEFAULT_RDP_PORT);
  const value = (rawHost ?? "").trim();
  if (!value) return { host: "", port: fallbackPort };

  const bracketed = value.match(/^\[([^\]]+)\](?::(\d+))?$/);
  if (bracketed) return { host: bracketed[1], port: bracketed[2] ?? fallbackPort };

  const colonCount = (value.match(/:/g) ?? []).length;
  if (colonCount === 1) {
    const [hostPart, portPart] = value.split(":");
    if (hostPart && /^\d+$/.test(portPart ?? "")) return { host: hostPart.trim(), port: portPart.trim() };
  }

  return { host: value, port: fallbackPort };
}

function validRdpPort(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const port = Number(value);
  return Number.isInteger(port) && port >= 1 && port <= 65535 ? port : null;
}

function rdpDestination(host: string, port: number): string {
  const h = host.trim();
  const normalizedHost = h.includes(":") && !(h.startsWith("[") && h.endsWith("]")) ? `[${h}]` : h;
  return port === DEFAULT_RDP_PORT ? normalizedHost : `${normalizedHost}:${port}`;
}

function normalizeStoragePart(value: string | number | null | undefined): string {
  return String(value ?? "")
    .trim()
    .replace(/[^a-zA-Z0-9_.:-]+/g, "_")
    .slice(0, 80);
}

/** Session Terminal Services occupante (issue de `rdp:list_sessions`). */
interface BusySession {
  sessionId: number;
  user: string;
  domain: string;
  state: string;
  isConsole: boolean;
  sessionName: string;
}

interface RdpRelayChannel {
  ready(): Promise<void>;
  send(msg: RdpJsonMessage): void;
  sendBinary(data: ArrayBuffer): void;
  on(predicate: (msg: RdpJsonMessage) => boolean, handler: RdpJsonHandler): () => void;
  onBinary(handler: RdpBinaryHandler): () => void;
  onDisconnect(handler: (reason: string) => void): () => void;
  isOpen(): boolean;
  close(reason?: string): void;
}

async function fetchRdpWsToken(): Promise<string> {
  await refreshRuntimeConfig();
  const res = await apiFetch("/api/ws-token", { credentials: "include" });
  if (!res.ok) throw new Error("Impossible d'obtenir un token WS RDP.");
  const data = (await res.json()) as { token?: string };
  if (!data.token) throw new Error("Token WS RDP absent.");
  return data.token;
}

type DedicatedMuxHandshakeMessage = {
  type: "mux.handshake";
  version?: number;
  peerIndex?: number;
  peerIdx?: number;
  agentPeerIndex?: number;
  agentPeerIdx?: number;
  maxPayloadBytes?: number;
  relayRoute?: boolean;
  agentMuxReady?: boolean;
  channels?: string[];
};

type DedicatedMuxAgentReadyMessage = {
  type: "mux.agent_ready";
  version?: number;
  agentPeerIndex?: number;
  agentPeerIdx?: number;
  relayRoute?: boolean;
};

function readMuxU16(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 0xffff ? value : null;
}

function readMuxMaxPayloadBytes(value: unknown): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 && value <= MUX_DEFAULT_MAX_PAYLOAD_BYTES
    ? value
    : MUX_DEFAULT_MAX_PAYLOAD_BYTES;
}

function rdpStreamIdForMessage(msg: RdpJsonMessage): number {
  const tunnelId = (msg as { tunnelId?: unknown }).tunnelId;
  if (typeof tunnelId !== "string") return 0;
  return rdpStreamIdFromTunnelId(tunnelId) ?? 0;
}

function createGlobalRdpRelayChannel(ws: ReturnType<typeof useWs>): RdpRelayChannel {
  return {
    ready: async () => {
      if (!ws.isOpen()) throw new Error("WebSocket opérateur non connecté.");
    },
    send: (msg) => ws.send(msg as never),
    sendBinary: (data) => ws.sendBinary(data),
    on: (predicate, handler) =>
      ws.on(
        (m) => predicate(m as RdpJsonMessage),
        (m) => handler(m as RdpJsonMessage),
      ),
    onBinary: (handler) => ws.onBinary(handler),
    onDisconnect: () => () => {},
    isOpen: () => ws.isOpen(),
    close: () => {},
  };
}

function createDedicatedRdpRelayChannel(sessionCode: string): RdpRelayChannel {
  const jsonHandlers = new Set<{ predicate: (msg: RdpJsonMessage) => boolean; handler: RdpJsonHandler }>();
  const binaryHandlers = new Set<RdpBinaryHandler>();
  const disconnectHandlers = new Set<(reason: string) => void>();
  const muxEnabled = isMuxBrowserEnabled();
  let socket: WebSocket | null = null;
  let connectPromise: Promise<void> | null = null;
  let joined = false;
  let closedByPanel = false;
  let muxReady = false;
  let muxRelayRoute = false;
  let muxAgentReady = false;
  let muxAgentPeerIndex: number | null = null;
  let muxMaxPayloadBytes = MUX_DEFAULT_MAX_PAYLOAD_BYTES;

  function dispatchJson(msg: RdpJsonMessage) {
    for (const entry of jsonHandlers) {
      try {
        if (entry.predicate(msg)) entry.handler(msg);
      } catch (err) {
        console.error("[rdp dedicated ws handler]", err);
      }
    }
  }

  function notifyDisconnect(reason: string) {
    if (closedByPanel) return;
    for (const handler of disconnectHandlers) {
      try {
        handler(reason);
      } catch (err) {
        console.error("[rdp dedicated ws disconnect]", err);
      }
    }
  }

  function canUseMux(): boolean {
    return muxEnabled && muxReady && muxRelayRoute && muxAgentReady && muxAgentPeerIndex !== null;
  }

  function negotiatedMuxChannels(handshake: DedicatedMuxHandshakeMessage): string[] {
    const local = muxBrowserReadyChannels();
    if (!Array.isArray(handshake.channels)) return local;
    const relayChannels = new Set(
      handshake.channels.filter((channel): channel is string => typeof channel === "string"),
    );
    return local.filter((channel) => relayChannels.has(channel));
  }

  function sendMuxReady(handshake: DedicatedMuxHandshakeMessage, targetSocket: WebSocket): void {
    targetSocket.send(
      JSON.stringify({
        type: "mux.ready",
        version: 1,
        peerIndex: readMuxU16(handshake.peerIndex ?? handshake.peerIdx),
        agentPeerIndex: muxAgentPeerIndex,
        maxPayloadBytes: muxMaxPayloadBytes,
        channels: negotiatedMuxChannels(handshake),
        flags: ["json_payload_v1"],
      }),
    );
  }

  function handleMuxControlPayload(payload: ArrayBuffer): void {
    let msg: RdpJsonMessage;
    try {
      msg = decodeMuxJsonPayload<RdpJsonMessage>(payload);
    } catch {
      return;
    }
    dispatchJson(msg);
  }

  function handleMuxFrame(data: ArrayBuffer): boolean {
    if (!muxEnabled || !isMuxFrame(data)) return false;
    try {
      const frame = decodeMuxFrame(data, { maxPayloadBytes: muxMaxPayloadBytes });
      if (frame.header.channel === MuxChannel.Rdp) {
        for (const handler of binaryHandlers) handler(frame.payload);
        return true;
      }
      if (frame.header.channel === MuxChannel.Control) {
        handleMuxControlPayload(frame.payload);
        return true;
      }
      return true;
    } catch (err) {
      console.warn(`[rdp mux dedicated] frame ignorée : ${err instanceof Error ? err.message : String(err)}`);
      return true;
    }
  }

  function handleMuxMessage(msg: RdpJsonMessage): boolean {
    const type = (msg as { type?: unknown }).type;
    if (type === "mux.handshake") {
      const handshake = msg as DedicatedMuxHandshakeMessage;
      if (!muxEnabled || handshake.version !== 1 || !socket || socket.readyState !== WebSocket.OPEN) return false;
      muxAgentPeerIndex = readMuxU16(handshake.agentPeerIndex ?? handshake.agentPeerIdx);
      muxMaxPayloadBytes = readMuxMaxPayloadBytes(handshake.maxPayloadBytes);
      muxRelayRoute = handshake.relayRoute === true;
      muxAgentReady = handshake.agentMuxReady === true;
      muxReady = true;
      sendMuxReady(handshake, socket);
      return true;
    }
    if (type === "mux.agent_ready") {
      const ready = msg as DedicatedMuxAgentReadyMessage;
      if (!muxEnabled || ready.version !== 1) return false;
      const nextAgentPeerIndex = readMuxU16(ready.agentPeerIndex ?? ready.agentPeerIdx);
      if (nextAgentPeerIndex !== null) muxAgentPeerIndex = nextAgentPeerIndex;
      if (ready.relayRoute === true) muxRelayRoute = true;
      muxAgentReady = true;
      return true;
    }
    return false;
  }

  async function waitForMuxReady(): Promise<void> {
    if (!muxEnabled) return;
    const deadline = Date.now() + 10_000;
    while (Date.now() < deadline) {
      if (canUseMux()) return;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    throw new Error("MUX RDP non prêt sur la WebSocket dédiée.");
  }

  async function connect(): Promise<void> {
    if (joined && socket?.readyState === WebSocket.OPEN) return;
    if (connectPromise) return connectPromise;
    connectPromise = (async () => {
      const token = await fetchRdpWsToken();
      const urls = wsUrlList();
      let lastError: unknown = null;
      for (const url of urls) {
        try {
          await new Promise<void>((resolve, reject) => {
            let settled = false;
            const timeout = setTimeout(() => {
              if (settled) return;
              settled = true;
              reject(new Error("Timeout de jonction WS RDP."));
              try {
                socket?.close(1000, "rdp_join_timeout");
              } catch {
                /* ignore */
              }
            }, 10_000);
            socket = new WebSocket(url, [`jwt.${token}`]);
            socket.binaryType = "arraybuffer";
            socket.addEventListener("open", () => {
              socket?.send(JSON.stringify({ type: "session:join", role: "operator", sessionCode, label: "RDP" }));
            });
            socket.addEventListener("message", (evt) => {
              if (evt.data instanceof ArrayBuffer) {
                if (handleMuxFrame(evt.data)) return;
                if (!muxEnabled) {
                  for (const handler of binaryHandlers) handler(evt.data);
                }
                return;
              }
              let msg: RdpJsonMessage;
              try {
                const raw = typeof evt.data === "string" ? evt.data : new TextDecoder().decode(evt.data as ArrayBuffer);
                msg = JSON.parse(raw) as RdpJsonMessage;
              } catch {
                return;
              }
              if (handleMuxMessage(msg)) return;
              if (msg.type === "session:joined" && msg.sessionCode === sessionCode) {
                joined = true;
                if (!settled) {
                  settled = true;
                  clearTimeout(timeout);
                  resolve();
                }
              }
              dispatchJson(msg);
            });
            socket.addEventListener("error", () => {
              if (!settled) {
                settled = true;
                clearTimeout(timeout);
                reject(new Error(`Erreur WS RDP sur ${url}.`));
              }
            });
            socket.addEventListener("close", (ev) => {
              const reason = ev.reason || `code ${ev.code}`;
              if (!settled) {
                settled = true;
                clearTimeout(timeout);
                reject(new Error(`WS RDP fermé avant jonction (${reason}).`));
              } else {
                joined = false;
                notifyDisconnect(`WS RDP fermé (${reason}).`);
              }
            });
          });
          return;
        } catch (err) {
          lastError = err;
          try {
            socket?.close(1000, "rdp_relay_retry");
          } catch {
            /* ignore */
          }
          socket = null;
          joined = false;
          muxReady = false;
          muxRelayRoute = false;
          muxAgentReady = false;
          muxAgentPeerIndex = null;
        }
      }
      throw lastError instanceof Error ? lastError : new Error("Connexion WS RDP impossible.");
    })().finally(() => {
      connectPromise = null;
    });
    return connectPromise;
  }

  return {
    ready: async () => {
      await connect();
      await waitForMuxReady();
    },
    send: (msg) => {
      if (socket?.readyState !== WebSocket.OPEN) return;
      if (muxEnabled) {
        if (!canUseMux()) return;
        socket.send(
          encodeMuxJsonFrame(
            {
              channel: MuxChannel.Control,
              toPeerIdx: muxAgentPeerIndex!,
              streamId: rdpStreamIdForMessage(msg),
              routingScope: MuxRoutingScope.UnicastPeer,
            },
            msg,
            { maxPayloadBytes: muxMaxPayloadBytes },
          ),
        );
        return;
      }
      socket.send(JSON.stringify(msg));
    },
    sendBinary: (data) => {
      if (socket?.readyState !== WebSocket.OPEN) return;
      if (muxEnabled) {
        if (!canUseMux()) return;
        socket.send(
          encodeMuxFrame(
            {
              channel: MuxChannel.Rdp,
              toPeerIdx: muxAgentPeerIndex!,
              streamId: rdpStreamIdFromFrame(data),
              routingScope: MuxRoutingScope.UnicastPeer,
            },
            data,
            { maxPayloadBytes: muxMaxPayloadBytes },
          ),
        );
        return;
      }
      socket.send(data);
    },
    on: (predicate, handler) => {
      const entry = { predicate, handler };
      jsonHandlers.add(entry);
      return () => jsonHandlers.delete(entry);
    },
    onBinary: (handler) => {
      binaryHandlers.add(handler);
      return () => binaryHandlers.delete(handler);
    },
    onDisconnect: (handler) => {
      disconnectHandlers.add(handler);
      return () => disconnectHandlers.delete(handler);
    },
    isOpen: () => socket?.readyState === WebSocket.OPEN && joined && (!muxEnabled || canUseMux()),
    close: (reason = "rdp-panel-unmount") => {
      closedByPanel = true;
      try {
        socket?.close(1000, reason);
      } catch {
        /* ignore */
      }
      socket = null;
      joined = false;
    },
  };
}

// ── Capture des logs IronRDP (visible dans le panneau) ─────────────────────────
//
// IronRDP-web initialise `tracing` avec `tracing-web::MakeConsoleWriter` :
// tous ses logs `tracing::debug!`/`info!`/`warn!`/`error!` arrivent dans
// `console.debug`/`info`/`warn`/`error`. On installe ici un patch idempotent
// qui pousse chaque log dans un buffer FIFO partagé entre toutes les instances
// du panneau, sans casser les outils dev (les originaux sont appelés en
// passthrough). Quand le panneau démontre, on ne désinstalle pas — un autre
// montage potentiel doit pouvoir réutiliser le buffer.
const LOG_BUFFER_MAX = 200;
const ironLogBuffer: Array<{ level: string; t: number; line: string }> = [];
const ironLogSubscribers = new Set<() => void>();

function pushIronLog(level: string, args: unknown[]): void {
  const line = args
    .map((a) => {
      if (typeof a === "string") return a;
      if (a instanceof Error) return `${a.name}: ${a.message}`;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(" ");
  ironLogBuffer.push({ level, t: Date.now(), line });
  while (ironLogBuffer.length > LOG_BUFFER_MAX) ironLogBuffer.shift();
  for (const cb of ironLogSubscribers) {
    try {
      cb();
    } catch {
      /* ignore */
    }
  }
}

// Installation idempotente du hook (s'exécute une seule fois par onglet).
type ConsoleHooked = Console & { __rdpHooked?: boolean };
function installConsoleCapture(): void {
  const c = console as ConsoleHooked;
  if (c.__rdpHooked) return;
  c.__rdpHooked = true;
  for (const lvl of ["log", "debug", "info", "warn", "error"] as const) {
    const orig = c[lvl].bind(c);
    c[lvl] = (...args: unknown[]) => {
      try {
        pushIronLog(lvl, args);
      } catch {
        /* ignore */
      }
      orig(...args);
    };
  }
}

/**
 * Convertit en chaîne lisible une erreur remontée par IronRDP-web.
 *
 * `IronError` est une classe wasm-bindgen avec `backtrace()` et `kind()` mais
 * pas de `Symbol.toPrimitive` — `String(err)` produit donc le sinistre
 * `"[object Object]"`. On préfère `err.backtrace()` quand disponible (le
 * message complet « anyhow »), puis `err.message`, puis un fallback générique.
 */
function describeIronError(err: unknown): string {
  if (err && typeof err === "object") {
    const e = err as { backtrace?: () => string; kind?: () => unknown; message?: unknown };
    if (typeof e.backtrace === "function") {
      try {
        const trace = e.backtrace();
        if (trace) {
          const kind = typeof e.kind === "function" ? String(e.kind()) : "";
          return kind ? `[${kind}] ${trace}` : trace;
        }
      } catch {
        /* tombe sur le fallback ci-dessous */
      }
    }
    if (typeof e.message === "string" && e.message) return e.message;
  }
  return err instanceof Error ? err.message : String(err);
}

/**
 * Charge le module IronRDP-web vendoré (`src/operator/vendor/ironrdp/`).
 *
 * L'import est DYNAMIQUE pour code-splitter le ~5 Mo de WASM : il n'est
 * téléchargé que lorsque l'opérateur ouvre le panneau RDP, pas au démarrage de
 * la console. Vite bundle la glue JS et l'artefact `_bg.wasm` dans un chunk
 * séparé (le glue résout le `.wasm` via `new URL('…_bg.wasm', import.meta.url)`,
 * motif reconnu par Vite). L'initialisation du WASM (`default()` =
 * `__wbg_init`) reste à la charge de l'appelant.
 */
async function loadIronRdp(): Promise<IronRdpModule> {
  const mod = await import("@/vendor/ironrdp/ironrdp_web.js");
  return mod as unknown as IronRdpModule;
}

interface RdpPanelProps {
  /** Session RemoteWS cible. Si fournie, le RDP utilise une WebSocket dédiée
   *  afin de survivre au changement d'onglet host dans la console. */
  sessionCode?: string | null;
  /** Visibilité effective du conteneur. Quand le panneau redevient visible,
   *  on force un recalage du canvas IronRDP. */
  visible?: boolean;
  /** Valeur initiale du champ "Hôte RDP" — utile quand le panel est ouvert
   *  depuis la page Prise en main et qu'on cible le hôte courant (typiquement
   *  127.0.0.1:3389 puisque l'IronRDP tunnel passe par l'agent sur l'hôte). */
  defaultHost?: string;
  /** Valeur initiale du champ "Port RDP". */
  defaultPort?: string | number;
  /** Valeur initiale du champ "Domaine". */
  defaultDomain?: string;
  /** Valeur initiale du champ "Utilisateur". */
  defaultUsername?: string;
  /** Credential vault à utiliser pour demander un grant RDP direct. */
  defaultCredentialId?: string;
  /** Préférence credential par usage pour le host (pré-sélection VOLT). */
  vaultDefaults?: Record<string, string>;
  /** « WS dans RDP » — bascule la capture en WS (WGC) sur une session TS précise.
   *  Fourni par le parent (panel vidéo) : déclenche un `video:control start` avec
   *  `targetSessionId` puis affiche le flux WS. Absent ⇒ bouton masqué. */
  onCaptureSessionWs?: (sessionId: number, label: string) => void;
}

const RdpPanel: Component<RdpPanelProps> = (props) => {
  const ws = useWs();
  // `sessionCode` est l'identité du canal RDP pour cette instance. Les callers
  // keyent/remontent le composant quand la cible change.
  /* eslint-disable solid/reactivity */
  const relay = props.sessionCode ? createDedicatedRdpRelayChannel(props.sessionCode) : createGlobalRdpRelayChannel(ws);
  /* eslint-enable solid/reactivity */

  // ── État ────────────────────────────────────────────────────────────────────
  const [status, setStatus] = createSignal<RdpStatus>("idle");
  const [error, setError] = createSignal<string | null>(null);
  // Sessions TS occupantes : remplies par `rdp:list_sessions` quand l'agent
  // signale un arbitrage (rdp:error code=rdp_session_busy) ou sur demande
  // manuelle depuis la barre d'outils. Pilotent la modale de libération.
  const [busySessions, setBusySessions] = createSignal<BusySession[]>([]);
  const [busySessionsOpen, setBusySessionsOpen] = createSignal(false);
  const [busySessionsLoading, setBusySessionsLoading] = createSignal(false);
  const [busySessionsError, setBusySessionsError] = createSignal<string | null>(null);
  const [closingSessionId, setClosingSessionId] = createSignal<number | null>(null);
  const confirm = useConfirm();
  const [barVisible, setBarVisible] = createSignal(true);
  const [toolbarMode, setToolbarMode] = createSignal<RdpToolbarMode>("expanded");
  const [toolbarPos, setToolbarPos] = createSignal<RdpToolbarPosition>(TOOLBAR_DEFAULT_POSITION);
  const [logsVisible, setLogsVisible] = createSignal(false);
  const [qualityProfile, setQualityProfile] = createSignal<RdpQualityProfile>(readQualityProfile());
  const [cmdKeyMode, setCmdKeyMode] = createSignal<RdpCmdKeyMode>(readCmdKeyMode());
  // Profil clavier persisté via le hook partagé. Lit le profil réel (mac/windows/
  // custom) depuis localStorage, pas depuis cmdKeyMode. L'éditeur peut activer
  // "custom" qui charge sa table éditable. cmdKeyMode reste pour le sélecteur
  // legacy dans la view de connexion (réutilise le même storage key).
  const kbProfile = useKeyboardProfile();
  const keyboardProfile = kbProfile.profile;
  // Éditeur de profil clavier (modal partagé).
  const [profileEditorOpen, setProfileEditorOpen] = createSignal(false);
  const activeProfileId = kbProfile.activeProfileId;
  const onProfileChange = kbProfile.setActiveProfileId;
  // Moteur clipboard partagé pour l'UI (historique + mode + feedback). Le
  // transport est un stub : les vraies actions CLIPRDR passent par les fonctions
  // locales (`pasteLocalClipboardIntoRemote`, `writeBrowserClipboardData`) qui
  // alimentent ensuite l'historique du moteur via `pushHistory`. Cela évite de
  // réécrire la logique CLIPRDR qui marche (Phase A).
  // `clipboardMode` / `setClipboardMode` aliasent le mode du moteur partagé :
  // tout le code CLIPRDR existant lit `clipboardMode()` sans changement, et le
  // menu partagé modifie le même état via `clipboardEngine.setMode`.
  const clipboardTransport: ClipboardTransport = {
    supportsRichText: () => true, // CLIPRDR gère HTML/PNG nativement.
    supportsFiles: () => false,
    async pushToRemote() {
      return true; // Le vrai push se fait via pasteLocalClipboardIntoRemote.
    },
    async requestFromRemote() {
      return null; // Le pull arrive via remoteClipboardChangedCallback.
    },
  };
  const clipboardEngine = createClipboardEngine(clipboardTransport);
  const clipboardMode = clipboardEngine.mode;
  const setClipboardMode = clipboardEngine.setMode;
  // Signal pour l'ouverture du menu clipboard partagé.
  const [clipboardMenuOpen, setClipboardMenuOpen] = createSignal(false);
  // Toggle Unicode : ON = caractères envoyés en unicode (respect du layout Mac,
  // mais casse PowerShell/PSReadLine qui ignore WM_CHAR). OFF = scancodes (layout
  // du Windows distant, marche partout y compris PowerShell). Par défaut ON.
  const [unicodeMode, setUnicodeMode] = createSignal<boolean>(
    (() => {
      try {
        return localStorage.getItem(preferenceStorageKey("unicodeMode")) !== "0";
      } catch {
        return true;
      }
    })(),
  );
  createEffect(() => {
    try {
      localStorage.setItem(preferenceStorageKey("unicodeMode"), unicodeMode() ? "1" : "0");
    } catch {
      /* localStorage indisponible */
    }
  });
  // Fenêtre de saisie : textarea locale où l'opérateur tape son texte, puis on
  // l'envoie comme un collage (CLIPRDR + Ctrl+V). Marche dans PowerShell car le
  // collage utilise WM_PASTE, pas WM_CHAR. Respecte le layout du navigateur.
  const [typeBoxOpen, setTypeBoxOpen] = createSignal(false);
  const [typeBoxText, setTypeBoxText] = createSignal("");
  const [typeBoxSending, setTypeBoxSending] = createSignal(false);
  const [audioEnabled, setAudioEnabled] = createSignal(false);
  const [isFullscreen, setIsFullscreen] = createSignal(false);
  const [detached, setDetached] = createSignal(false);
  // `logsVersion` est un compteur qui s'incrémente à chaque nouveau log :
  // c'est la dépendance réactive qui force le `<For>` à se re-rendre, le
  // tableau `ironLogBuffer` étant mutable et partagé (pas un signal lui-même).
  const [logsVersion, setLogsVersion] = createSignal(0);
  // Snapshot réactif du buffer pour `<For>` : on clone à chaque bump de
  // version. Coût négligeable (max 200 entrées) et garantit la cohérence.
  const logsSnapshot = createMemo(() => {
    void logsVersion();
    return ironLogBuffer.slice();
  });

  // Formulaire de credentials Windows (jamais persisté, jamais loggé).
  const initialTarget = splitRdpHostPort(props.defaultHost, props.defaultPort);
  const [host, setHost] = createSignal(initialTarget.host);
  const [port, setPort] = createSignal(initialTarget.port);
  const [rdpCredResult, setRdpCredResult] = createSignal<CredentialInputResult>({
    mode: "manual",
    ready: false,
  });
  let appliedDefaults = `${props.defaultHost ?? ""}||${props.defaultPort ?? ""}`;

  // Met à jour dynamiquement les champs quand la cible RDP change.
  createEffect(() => {
    const nextHost = props.defaultHost ?? "";
    const nextPort = props.defaultPort ?? "";
    const nextSeed = `${nextHost}||${nextPort}`;
    if (nextSeed === appliedDefaults) return;
    appliedDefaults = nextSeed;
    const nextTarget = splitRdpHostPort(nextHost, nextPort);
    setHost(nextTarget.host);
    setPort(nextTarget.port);
    setQualityProfile(readQualityProfile());
    setCmdKeyMode(readCmdKeyMode());
  });

  // ── Refs / handles non réactifs ─────────────────────────────────────────────
  let stageHomeEl: HTMLDivElement | undefined;
  let stageEl: HTMLDivElement | undefined;
  let canvasEl: HTMLCanvasElement | undefined;
  let surfaceEl: HTMLDivElement | undefined;
  let toolbarEl: HTMLDivElement | undefined;
  let tunnelId: string | null = null;
  let tunnelSocket: RdpTunnelSocket | null = null;
  let iron: IronRdpModule | null = null;
  let session: IronSession | null = null;
  let barIdleTimer: ReturnType<typeof setTimeout> | null = null;
  let toolbarDrag: { pointerId: number; x: number; y: number; ox: number; oy: number; moved: boolean } | null = null;
  let toolbarSuppressClick = false;
  const suppressedKeyUps = new Set<string>();
  // Track `e.code → caractère unicode pressé` pour pouvoir appeler
  // `unicodeReleased` au keyup avec le BON caractère. Sans ça, chaque caractère
  // unicode reste "enfoncé" côté Windows → wedge les terminaux (PowerShell,
  // conhost) qui tracent strictement les transitions down/up.
  const unicodePressedCodes = new Map<string, string>();
  let detachWindow: Window | null = null;
  let detachPoll: ReturnType<typeof setInterval> | null = null;
  let lastDesktopResize: { width: number; height: number } | null = null;
  let resizeGeneration = 0;
  let settledResizeTimers: ReturnType<typeof setTimeout>[] = [];
  let restoreTunnelSocket: (() => void) | null = null;
  let tunnelOpenSent = false;
  let tunnelCloseHandled = false;
  let teardownInProgress = false;
  let lastAgentRdpError: string | null = null;

  const featureRdp = () =>
    props.sessionCode && ws.state.sessionCode !== props.sessionCode ? true : ws.state.agentFeatures?.rdp === true;

  // ── Barre flottante auto-fade (calqué sur video.tsx) ────────────────────────
  const BAR_IDLE_MS = 2500;
  function panelStorageScope(): string {
    return (
      props.sessionCode ||
      `${normalizeStoragePart(props.defaultHost)}:${normalizeStoragePart(props.defaultPort ?? DEFAULT_RDP_PORT)}` ||
      "default"
    );
  }

  function toolbarStorageKey(): string {
    return `rws.rdp.toolbar.${panelStorageScope()}`;
  }

  function preferenceStorageKey(name: string): string {
    return `rws.rdp.${name}.${panelStorageScope()}`;
  }

  function defaultCmdKeyMode(): RdpCmdKeyMode {
    if (typeof navigator === "undefined") return "windows";
    return defaultCmdKeyModeForPlatform(navigator.platform, navigator.userAgent);
  }

  function readCmdKeyMode(): RdpCmdKeyMode {
    try {
      const value = localStorage.getItem(preferenceStorageKey("cmdKeyMode"));
      if (value === "local" || value === "windows") return value;
    } catch {
      /* localStorage indisponible — défaut plateforme. */
    }
    return defaultCmdKeyMode();
  }

  function setCmdKeyModeValue(mode: RdpCmdKeyMode) {
    try {
      localStorage.setItem(preferenceStorageKey("cmdKeyMode"), mode);
    } catch {
      /* localStorage indisponible — réglage non persisté. */
    }
    setCmdKeyMode(mode);
    releaseRemoteInputs();
  }

  function readQualityProfile(): RdpQualityProfile {
    try {
      const value = localStorage.getItem(preferenceStorageKey("qualityProfile"));
      if (value === "eco" || value === "standard" || value === "high" || value === "native") return value;
    } catch {
      /* localStorage indisponible — qualité non persistée. */
    }
    return "high";
  }

  function setQualityProfileValue(profile: RdpQualityProfile) {
    try {
      localStorage.setItem(preferenceStorageKey("qualityProfile"), profile);
    } catch {
      /* localStorage indisponible — réglage non persisté. */
    }
    setQualityProfile(profile);
    scheduleSettledSurfaceResize(true);
  }

  function persistToolbarPosition(pos = toolbarPos()) {
    try {
      localStorage.setItem(toolbarStorageKey(), JSON.stringify(pos));
    } catch {
      /* localStorage indisponible — position non persistée. */
    }
  }

  function restoreToolbarPosition() {
    try {
      const raw = localStorage.getItem(toolbarStorageKey());
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<RdpToolbarPosition>;
      if (typeof parsed.x === "number" && typeof parsed.y === "number") {
        setToolbarPos({ x: parsed.x, y: parsed.y });
      }
    } catch {
      /* position invalide — on garde le défaut. */
    }
  }

  function clampToolbarPosition(next = toolbarPos()): RdpToolbarPosition {
    const bounds = {
      width: stageEl?.clientWidth || surfaceEl?.clientWidth || 1,
      height: stageEl?.clientHeight || surfaceEl?.clientHeight || 1,
    };
    const toolbar = {
      width: toolbarEl?.offsetWidth || (toolbarMode() === "mini" ? 44 : 360),
      height: toolbarEl?.offsetHeight || (toolbarMode() === "mini" ? 44 : 48),
    };
    return clampRdpToolbarPosition(next, bounds, toolbar, TOOLBAR_MARGIN);
  }

  function setClampedToolbarPosition(next: RdpToolbarPosition, persist = false) {
    const clamped = clampToolbarPosition(next);
    setToolbarPos(clamped);
    if (persist) persistToolbarPosition(clamped);
  }

  function reclampToolbarPosition(persist = false) {
    setClampedToolbarPosition(toolbarPos(), persist);
  }

  function armBarIdleTimer() {
    if (barIdleTimer) clearTimeout(barIdleTimer);
    if (status() !== "connected") return;
    barIdleTimer = setTimeout(() => setBarVisible(false), BAR_IDLE_MS);
  }
  function wakeBar() {
    if (!barVisible()) setBarVisible(true);
    armBarIdleTimer();
  }

  function beginToolbarDrag(e: PointerEvent) {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    wakeBar();
    const current = toolbarPos();
    toolbarDrag = { pointerId: e.pointerId, x: e.clientX, y: e.clientY, ox: current.x, oy: current.y, moved: false };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  }

  function moveToolbarDrag(e: PointerEvent) {
    if (!toolbarDrag || toolbarDrag.pointerId !== e.pointerId) return;
    e.preventDefault();
    e.stopPropagation();
    const dx = e.clientX - toolbarDrag.x;
    const dy = e.clientY - toolbarDrag.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) toolbarDrag.moved = true;
    setClampedToolbarPosition({ x: toolbarDrag.ox + dx, y: toolbarDrag.oy + dy });
  }

  function endToolbarDrag(e: PointerEvent) {
    if (!toolbarDrag || toolbarDrag.pointerId !== e.pointerId) return;
    e.preventDefault();
    e.stopPropagation();
    toolbarSuppressClick = toolbarDrag.moved;
    persistToolbarPosition();
    toolbarDrag = null;
  }

  function setToolbarModeValue(mode: RdpToolbarMode) {
    setToolbarMode(mode);
    requestAnimationFrame(() => reclampToolbarPosition(true));
    wakeBar();
  }

  function desiredDesktopSize(): { width: number; height: number } {
    // On mesure la surface en PIXELS CSS (logiques), PAS en pixels physiques.
    // Raison : la résolution RDP demandée correspond à la taille LOGIQUE du bureau
    // distant. La multiplier par devicePixelRatio (×2 sur Retina) demanderait un
    // bureau 5K pour une fenêtre 2560×1440 → le serveur RDP encode/capture 4× trop
    // de bitmaps → lenteur manifeste (clics/mouvements) sans gain de netteté, car
    // le navigateur downscale quand même le backing store vers l'affichage.
    //
    // Le navigateur assure ensuite le zoom d'affichage via object-fit: contain :
    // un bureau 1536×864 (profil éco) est affiché plein cadre et zoomé — c'est le
    // comportement attendu "résolution plus petite + zoomée" sur grand écran.
    // C'est différent de video.tsx (qui mesure en px physiques) parce que video
    // encode en H.264 (la compression absorbe les pixels) alors que le RDP
    // transporte des bitmaps bruts (chaque pixel coûte sur le wire).
    const rect = surfaceEl?.getBoundingClientRect();
    let cssWidth = rect?.width && rect.width > 0 ? rect.width : canvasEl?.clientWidth || 1280;
    let cssHeight = rect?.height && rect.height > 0 ? rect.height : canvasEl?.clientHeight || 720;
    // Garde-fou défensif (signalement 4bfa3390) : si la surface déborde de la
    // zone visible — par ex. en mode fenêtré quand la chaîne flex n'a pas encore
    // convergé, ou dans un layout exotique — on borne la résolution demandée à la
    // portion réellement visible. Demander un bureau plus grand que ce que
    // l'opérateur peut voir rogne la barre des tâches Windows, puisque le backing
    // store IronRDP vaut exactement la résolution demandée et que le canvas CSS
    // occupe ensuite height:100% de la surface débordante.
    const visibleHeight = rect ? Math.min(rect.height, window.innerHeight - rect.top) : cssHeight;
    const visibleWidth = rect ? Math.min(rect.width, window.innerWidth - rect.left) : cssWidth;
    if (visibleHeight > 0 && visibleHeight < cssHeight) cssHeight = visibleHeight;
    if (visibleWidth > 0 && visibleWidth < cssWidth) cssWidth = visibleWidth;
    const dpr = window.devicePixelRatio || 1;
    const raw = rdpDesktopSizeForSurface({ width: cssWidth, height: cssHeight }, qualityProfile(), dpr, {
      minWidth: RDP_MIN_WIDTH,
      minHeight: RDP_MIN_HEIGHT,
      maxWidth: RDP_MAX_WIDTH,
      maxHeight: RDP_MAX_HEIGHT,
    });
    // clampRdpDesktopSize force la largeur PAIRE et les bornes proto RDP :
    // une largeur impaire fait que le serveur rejette la PDU sans feedback.
    return clampRdpDesktopSize(raw);
  }

  function resizeRdpDesktop(size: { width: number; height: number }) {
    if (!session || !canvasEl) return;
    // Largeur forcée PAIRE + bornes proto (cf. clampRdpDesktopSize). Une largeur
    // impaire = PDU rejetée silencieusement par le serveur (MS-RDPEDISP §2.2.2.2.2).
    const next = clampRdpDesktopSize({ width: Math.round(size.width), height: Math.round(size.height) });
    if (lastDesktopResize && lastDesktopResize.width === next.width && lastDesktopResize.height === next.height) return;
    lastDesktopResize = next;
    // On NE touche PAS à canvasEl.width/height : IronRDP-web possède le backing
    // store. Il l'écrit lui-même à l'init (Canvas::new) ET au Deactivate-All PDU
    // reçu après un resize accepté (session.rs lignes 982-984 : set_width/height +
    // gui.resize). Toute écriture JS concurrence le WASM et provoque bandes /
    // framebuffer désynchronisé.
    //
    // PRÉREQUIS CRITIQUE : `session.resize` n'a d'effet QUE si le canal Display
    // Control est activé (builder.extension("display_control", true)). Sans lui,
    // `encode_resize` retourne None ("Display Control Virtual Channel is not
    // available") et le serveur ne reçoit jamais la demande → resize mort.
    // On n'envoie QUE (width, height). Les params optionnels de session.resize
    // — scale_factor et physical_width/height — sont DANGEREUX ici :
    //  - scale_factor est un POURCENTAGE entier ∈ [100,500] (MS-RDPEDISP), PAS un
    //    ratio. Lui passer devicePixelRatio (ex. 2) = 2% = "out of range" → la
    //    PDU est rejetée et le resize échoue avec une erreur de session.
    //  - physical_* attendent des MILLIMÈTRES ∈ [10,10000], or on n'a que des
    //    pixels côté navigateur.
    // On laisse donc le serveur à 100% de DPI ; le zoom visuel sur grand écran
    // opérateur est assuré par object-fit: contain (le canvas est mis à l'échelle
    // CSS, indépendamment de la résolution RDP demandée — cf. profils éco/standard).
    try {
      session.resize(next.width, next.height);
    } catch {
      // resize peut être refusé selon l'état RDP — sans gravité.
    }
  }

  function forceResizeRdpDesktop(size: { width: number; height: number }) {
    lastDesktopResize = null;
    resizeRdpDesktop(size);
  }

  function clearSettledResizeTimers() {
    for (const timer of settledResizeTimers) clearTimeout(timer);
    settledResizeTimers = [];
  }

  function applySurfaceResize(force = false) {
    if (status() !== "connected" || !session || !surfaceEl || !canvasEl) return;
    reclampToolbarPosition(true);
    const size = desiredDesktopSize();
    if (force) forceResizeRdpDesktop(size);
    else resizeRdpDesktop(size);
  }

  function scheduleSettledSurfaceResize(force = false) {
    // On ne déclenche plus plusieurs timers "espoir" : un seul recal immédiat
    // (raf + 0ms) suffit, la confirmation réelle arrive via `canvasResizedCallback`.
    clearSettledResizeTimers();
    const generation = ++resizeGeneration;
    const timer = setTimeout(() => {
      if (generation !== resizeGeneration) return;
      requestAnimationFrame(() => {
        if (generation !== resizeGeneration) return;
        applySurfaceResize(force);
      });
    }, 0);
    settledResizeTimers.push(timer);
  }

  async function readBrowserClipboardData(): Promise<IronClipboardData | null> {
    if (!iron || !navigator.clipboard) return null;
    const data = new iron.ClipboardData();
    const clip = navigator.clipboard as Clipboard & {
      read?: () => Promise<Array<{ types: readonly string[]; getType: (type: string) => Promise<Blob> }>>;
    };

    if (typeof clip.read === "function") {
      try {
        const items = await clip.read();
        for (const item of items) {
          if (item.types.includes("text/html")) {
            const blob = await item.getType("text/html");
            const html = await blob.text();
            if (html) data.addText("text/html", html);
          }
          if (item.types.includes("text/plain")) {
            const blob = await item.getType("text/plain");
            data.addText("text/plain", await blob.text());
          }
          if (item.types.includes("image/png")) {
            const blob = await item.getType("image/png");
            data.addBinary("image/png", new Uint8Array(await blob.arrayBuffer()));
          }
        }
      } catch {
        /* On retombe sur readText ci-dessous. */
      }
    }

    if (data.isEmpty() && typeof navigator.clipboard.readText === "function") {
      try {
        const text = await navigator.clipboard.readText();
        if (text) data.addText("text/plain", text);
      } catch {
        /* permission refusée ou API indisponible. */
      }
    }

    return data.isEmpty() ? null : data;
  }

  async function ironClipboardDataFromPasteEvent(e: ClipboardEvent): Promise<IronClipboardData | null> {
    if (!iron) return null;
    const clipboard = e.clipboardData;
    if (!clipboard) return null;
    const data = new iron.ClipboardData();
    const html = clipboard.getData("text/html");
    if (html) data.addText("text/html", html);
    const text = clipboard.getData("text/plain");
    if (text) data.addText("text/plain", text);

    for (const item of Array.from(clipboard.items ?? [])) {
      if (item.type !== "image/png") continue;
      const file = item.getAsFile();
      if (!file) continue;
      data.addBinary("image/png", new Uint8Array(await file.arrayBuffer()));
    }

    return data.isEmpty() ? null : data;
  }

  async function writeBrowserClipboardData(content: IronClipboardData) {
    if (!clipboardAllowsRemoteToLocal(clipboardMode()) || !navigator.clipboard) return;
    let text: string | null = null;
    let html: string | null = null;
    let png: Uint8Array | null = null;

    for (const item of content.items()) {
      const mime = item.mimeType();
      const value = item.value();
      if (mime === "text/plain" && typeof value === "string") {
        text = value;
      } else if (mime === "text/html" && typeof value === "string") {
        html = value;
      } else if (mime === "image/png") {
        if (value instanceof Uint8Array) png = value;
        else if (value instanceof ArrayBuffer) png = new Uint8Array(value);
      }
    }

    // Image PNG prioritaire (souvent la seule représentation d'une capture d'écran).
    if (png && typeof navigator.clipboard.write === "function" && typeof ClipboardItem !== "undefined") {
      try {
        const imageBuffer = new ArrayBuffer(png.byteLength);
        new Uint8Array(imageBuffer).set(png);
        await navigator.clipboard.write([
          new ClipboardItem({ "image/png": new Blob([imageBuffer], { type: "image/png" }) }),
        ]);
        return;
      } catch {
        /* On tente le texte si présent. */
      }
    }

    // HTML + texte : ClipboardItem accepte plusieurs types simultanés, ce qui
    // préserve le formatage riche (Word, Excel, pages web) tout en gardant une
    // chute texte. On privilégie cette voie quand on a au moins du HTML.
    if (html != null && typeof navigator.clipboard.write === "function" && typeof ClipboardItem !== "undefined") {
      try {
        const items: Record<string, Blob> = { "text/html": new Blob([html], { type: "text/html" }) };
        if (text != null) items["text/plain"] = new Blob([text], { type: "text/plain" });
        await navigator.clipboard.write([new ClipboardItem(items)]);
        return;
      } catch {
        /* On retombe sur writeText ci-dessous. */
      }
    }

    if (text != null && typeof navigator.clipboard.writeText === "function") {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        /* permission refusée. */
      }
    }
  }

  // ── État du handshake CLIPRDR ───────────────────────────────────────────────
  // `clipboardServerSignalEpoch` est incrémenté à CHAQUE invocation de
  // `forceClipboardUpdateCallback` — c'est-à-dire à chaque FormatDataRequest
  // envoyé par le serveur (étape 2 du MS-RDPECLIP Copy Sequence). Surveiller
  // son augmentation depuis le début d'un paste donne un signal fiable que le
  // FormatList a bien été reçu et que le serveur réclame le contenu.
  // `clipboardPasteEpoch` permet d'invalider un poll en cours si un nouveau
  // paste démarre (on n'envoie jamais un Ctrl+V pour un contenu périmé).
  let clipboardServerSignalEpoch = 0;
  let clipboardServerSignalAtMs = 0;
  let clipboardPasteEpoch = 0;
  let clipboardPasteInFlight = false;
  let clipboardServerSignalAtStart = 0;

  function nextClipboardDelayDecision(start: number): ReturnType<typeof resolveRemotePasteDelay> {
    const serverRequestedContent = clipboardServerSignalEpoch > clipboardServerSignalAtStart;
    const elapsedSincePasteMs = performance.now() - start;
    const elapsedSinceServerSignalMs = serverRequestedContent
      ? performance.now() - clipboardServerSignalAtMs
      : undefined;
    return resolveRemotePasteDelay({
      elapsedSincePasteMs,
      serverRequestedContent,
      elapsedSinceServerSignalMs,
    });
  }

  /**
   * Attend la fin du handshake CLIPRDR avant de lâcher le Ctrl+V synthétisé.
   * Voir `resolveRemotePasteDelay` dans `rdp-ui.ts` pour la logique détaillée.
   * Retourne `false` si le handshake doit être abandonné (timeout) ou si un
   * paste plus récent a démarré entre-temps (on est devenu obsolète).
   */
  async function waitForClipboardHandshake(pasteEpoch: number, start: number): Promise<boolean> {
    while (pasteEpoch === clipboardPasteEpoch) {
      const decision = nextClipboardDelayDecision(start);
      if (decision.kind === "ready") return true;
      if (decision.kind === "abort") return false;
      await new Promise((r) => setTimeout(r, decision.waitMs));
    }
    return false;
  }

  async function pushClipboardToRemote(): Promise<IronClipboardData | null> {
    if (!session || !clipboardAllowsLocalToRemote(clipboardMode())) return null;
    const data = await readBrowserClipboardData();
    if (!data || data.isEmpty()) return null;
    try {
      await session.onClipboardPaste(data);
    } catch (err) {
      console.warn("[rdp] clipboard local→remote:", err);
      return null;
    }
    return data;
  }

  function sendRemoteCtrlV() {
    if (!iron || !session) return;
    try {
      const tx = new iron.InputTransaction();
      tx.addEvent(iron.DeviceEvent.keyPressed(RDP_CTRL_SCANCODE));
      tx.addEvent(iron.DeviceEvent.keyPressed(RDP_V_SCANCODE));
      tx.addEvent(iron.DeviceEvent.keyReleased(RDP_V_SCANCODE));
      tx.addEvent(iron.DeviceEvent.keyReleased(RDP_CTRL_SCANCODE));
      session.applyInputs(tx);
    } catch {
      /* best-effort */
    }
  }

  /**
   * Envoie le texte de la fenêtre de saisie comme un collage. Écrit le texte
   * dans le clipboard navigateur, puis déclenche le collage CLIPRDR + Ctrl+V.
   *
   * Avantage sur la frappe directe : le collage utilise WM_PASTE (pas WM_CHAR),
   * donc ça marche dans PowerShell/PSReadLine. Le layout du navigateur est
   * respecté (le texte tapé dans la textarea est interprété par le Mac).
   */
  async function sendTypeBoxText() {
    const text = typeBoxText();
    if (!text || typeBoxSending()) return;
    setTypeBoxSending(true);
    try {
      // Écrit dans le clipboard navigateur (format text/plain).
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        // Permission refusée : on tente quand même le paste (le handshake CLIPRDR
        // lira peut-être un clipboard stale, mais le Ctrl+V natif fera un collage).
      }
      // Déclenche le collage : push CLIPRDR + Ctrl+V.
      await pasteLocalClipboardIntoRemote();
      setTypeBoxOpen(false);
      setTypeBoxText("");
    } finally {
      setTypeBoxSending(false);
    }
  }

  /** Résultat d'un paste clipboard local→remote exploitable par l'appelant. */
  type RdpPasteResult =
    | { kind: "synced" } // FormatList + Ctrl+V envoyés avec succès.
    | { kind: "native-fallback" } // Clipboard sync impossible → Ctrl+V natif envoyé (colle le presse-papier du Windows distant).
    | { kind: "noop"; reason: "mode-disabled" | "empty" | "in-flight" | "no-session" }; // Rien à faire.

  /**
   * Pousse le clipboard local vers la session RDP puis envoie un Ctrl+V synthétisé,
   * en attendant la fin du handshake CLIPRDR (signal serveur + garde-fou) entre
   * les deux. Sans cette attente, le Ctrl+V arrive sur le serveur Windows avant
   * que les données ne soient dans son presse-papier → collage vide ou stale.
   *
   * Garde-fous :
   *  - re-entrant-safe via `clipboardPasteInFlight` (ignore les paste concurrents) ;
   *  - si la lecture clipboard échoue (vide/permission), on n'envoie PAS de Ctrl+V ;
   *  - timeout global (`RDP_CLIPBOARD_PASTE_TIMEOUT_MS`) pour ne jamais bloquer.
   *
   * Retourne un `RdpPasteResult` afin que l'appelant (typiquement `onKeyDown`)
   * puisse décider d'un fallback natif : si le transfert clipboard échoue
   * (permission refusée, handshake mort, etc.), on envoie quand même le scancode
   * Ctrl+V au Windows distant qui collera son PROPRE presse-papier. Sans ce
   * fallback, l'opérateur voit "Ctrl+V ne fait rien" quand la sync clipboard
   * échoue silencieusement.
   */
  async function pasteLocalClipboardIntoRemote(): Promise<RdpPasteResult> {
    if (!session || !iron) return { kind: "noop", reason: "no-session" };
    if (!clipboardAllowsLocalToRemote(clipboardMode())) return { kind: "noop", reason: "mode-disabled" };
    if (clipboardPasteInFlight) return { kind: "noop", reason: "in-flight" };

    clipboardPasteInFlight = true;
    const pasteEpoch = ++clipboardPasteEpoch;
    clipboardServerSignalAtStart = clipboardServerSignalEpoch;
    const start = performance.now();
    try {
      const data = await pushClipboardToRemote();
      if (!data || data.isEmpty()) return { kind: "noop", reason: "empty" }; // Rien à coller.

      // Alimente l'historique du moteur partagé DÈS la lecture du clipboard,
      // indépendamment du succès du handshake CLIPRDR. Même si le handshake
      // échoue (native-fallback), l'opérateur a tenté un transfert → on le log.
      const snapshot: ClipboardSnapshot = {};
      for (const item of data.items()) {
        const mime = item.mimeType();
        const value = item.value();
        if (mime === "text/plain" && typeof value === "string") snapshot.text = value;
        else if (mime === "text/html" && typeof value === "string") snapshot.html = value;
        else if (mime === "image/png" && value instanceof Uint8Array) snapshot.png = value;
      }
      clipboardEngine.pushHistory(entryFromSnapshot(snapshot, "local", Date.now(), `paste-${Date.now()}`));

      const ok = await waitForClipboardHandshake(pasteEpoch, start);
      if (!ok) {
        console.warn("[rdp] clipboard paste : handshake CLIPRDR non abouti, Ctrl+V natif");
        return { kind: "native-fallback" }; // Le serveur n'a pas reçu le contenu → Ctrl+V natif.
      }
      sendRemoteCtrlV();
      return { kind: "synced" };
    } catch (err) {
      console.warn("[rdp] clipboard local→remote:", err);
      return { kind: "native-fallback" }; // Erreur (permission/lecture) → Ctrl+V natif.
    } finally {
      if (pasteEpoch === clipboardPasteEpoch) clipboardPasteInFlight = false;
    }
  }

  async function pasteEventIntoRemote(e: ClipboardEvent) {
    if (!session || !clipboardAllowsLocalToRemote(clipboardMode())) return;
    e.preventDefault();
    // On délègue au chemin résilient : parse + onClipboardPaste + attente du
    // handshake + Ctrl+V. On conserve une parsing dédié car l'événement DOM
    // expose `clipboardData` (synchrone) que `navigator.clipboard.read()` ne
    // reflète pas toujours (notamment pour le HTML riche en paste contextuel).
    if (!iron || clipboardPasteInFlight) return;

    clipboardPasteInFlight = true;
    const pasteEpoch = ++clipboardPasteEpoch;
    clipboardServerSignalAtStart = clipboardServerSignalEpoch;
    const start = performance.now();
    try {
      const data = await ironClipboardDataFromPasteEvent(e);
      if (!data || data.isEmpty()) return;
      try {
        await session.onClipboardPaste(data);
      } catch (err) {
        console.warn("[rdp] clipboard paste event:", err);
        // Échec push → Ctrl+V natif pour coller le presse-papier du Windows distant.
        sendRemoteCtrlV();
        return;
      }
      const ok = await waitForClipboardHandshake(pasteEpoch, start);
      if (!ok) {
        console.warn("[rdp] clipboard paste : handshake CLIPRDR non abouti, Ctrl+V natif");
        sendRemoteCtrlV(); // Handshake mort → Ctrl+V natif (presse-papier du Windows distant).
        return;
      }
      sendRemoteCtrlV();
    } finally {
      if (pasteEpoch === clipboardPasteEpoch) clipboardPasteInFlight = false;
    }
  }

  function canvasContentRect(): { left: number; top: number; width: number; height: number } | null {
    if (!canvasEl) return null;
    const rect = canvasEl.getBoundingClientRect();
    const canvasW = canvasEl.width || rect.width;
    const canvasH = canvasEl.height || rect.height;
    if (rect.width <= 0 || rect.height <= 0 || canvasW <= 0 || canvasH <= 0) return null;
    const canvasRatio = canvasW / canvasH;
    const boxRatio = rect.width / rect.height;
    if (boxRatio > canvasRatio) {
      const height = rect.height;
      const width = height * canvasRatio;
      return {
        left: rect.left + (rect.width - width) / 2,
        top: rect.top,
        width,
        height,
      };
    }
    const width = rect.width;
    const height = width / canvasRatio;
    return {
      left: rect.left,
      top: rect.top + (rect.height - height) / 2,
      width,
      height,
    };
  }

  function sendTunnelCloseOnce(reason: string, expectedTunnelId: string | null = tunnelId) {
    if (!expectedTunnelId || expectedTunnelId !== tunnelId || !tunnelOpenSent || tunnelCloseHandled) return;
    tunnelCloseHandled = true;
    relay.send({ type: "rdp:close", tunnelId: expectedTunnelId, reason });
  }

  function sendTunnelControl(msg: RdpJsonMessage) {
    if (msg.type === "rdp:close") {
      const msgTunnelId = typeof msg.tunnelId === "string" ? msg.tunnelId : tunnelId;
      const reason = typeof msg.reason === "string" && msg.reason ? msg.reason : "wasm-close";
      sendTunnelCloseOnce(reason, msgTunnelId);
      return;
    }
    relay.send(msg);
  }

  // ── Handlers WS — réception des messages `rdp:*` du relais ──────────────────

  // Réception des données RDP : trames WebSocket BINAIRES (magic "RD").
  // Plus de base64, plus de parse JSON par chunk.
  const offData = relay.onBinary((buf) => {
    const frame = parseRdpFrame(buf);
    if (!frame) return; // pas une trame RDP (ex. vidéo "VB") — ignorée.
    // Filtrage par tunnelId : un autre tunnel ne doit pas alimenter celui-ci.
    if (!tunnelId || frame.tunnelId !== tunnelId) return;
    tunnelSocket?.feed(frame.payload);
  });

  const offClose = relay.on(
    (m) => m.type === "rdp:close",
    (m) => {
      const msg = m as { type: "rdp:close"; tunnelId?: string; reason?: string };
      if (!tunnelId || msg.tunnelId !== tunnelId) return;
      const text =
        msg.reason === "rdp_replaced"
          ? "Un autre tunnel RDP a remplacé cette connexion. Fermez la session RDP active avant de réessayer."
          : msg.reason
            ? `Session terminée : ${msg.reason}`
            : null;
      if (text) lastAgentRdpError = text;
      tunnelSocket?.failOrClose(1000, text ?? "fermé par l'agent");
      finishSession(text, false);
    },
  );

  const offError = relay.on(
    (m) => m.type === "rdp:error",
    (m) => {
      const msg = m as { type: "rdp:error"; tunnelId?: string; code?: string; message?: string };
      if (!tunnelId || msg.tunnelId !== tunnelId) return;
      const text =
        msg.code === "rdp_tunnel_active"
          ? "Un tunnel RDP est déjà actif pour cette cible et ce login. Fermez-le avant d'en ouvrir un autre."
          : (msg.message ?? "Erreur RDP côté agent");
      lastAgentRdpError = text;
      tunnelSocket?.failOrClose(1006, text);
      setError(text);
      setStatus("error");
      teardown(false);
      // Arbitrage de session Windows (fSingleSessionPerUser=1) : la socket RDP
      // a été fermée avant la fin du handshake. On propose à l'opérateur de
      // lister puis libérer la session occupante avant de réessayer.
      if (msg.code === "rdp_session_busy") {
        void fetchBusySessions(true);
      }
    },
  );

  // ── Sessions TS occupantes (libération avant retry RDP) ─────────────────────
  // L'agent répond à `rdp:list_sessions` par `rdp:sessions` et à
  // `rdp:close_session` par `rdp:session_closed`. Le relay route ces messages
  // de façon opaque (gate support_approved côté operator→endpoint).
  const offSessions = relay.on(
    (m) => m.type === "rdp:sessions",
    (m) => {
      const msg = m as {
        type: "rdp:sessions";
        requestId?: string;
        sessions?: unknown;
        error?: string;
      };
      if (msg.error) {
        setBusySessionsError(msg.error);
        setBusySessions([]);
      } else if (Array.isArray(msg.sessions)) {
        setBusySessions((msg.sessions as BusySession[]).filter((s) => s && typeof s.sessionId === "number"));
        setBusySessionsError(null);
      }
      setBusySessionsLoading(false);
    },
  );

  const offSessionClosed = relay.on(
    (m) => m.type === "rdp:session_closed",
    (m) => {
      const msg = m as {
        type: "rdp:session_closed";
        requestId?: string;
        ok?: boolean;
        sessionId?: number;
        action?: string;
        error?: string;
      };
      setClosingSessionId(null);
      if (msg.ok) {
        // Session libérée : retirer de la liste, puis réessayer la connexion RDP
        // si la modale a été ouverte suite à un arbitrage (autoRetry).
        setBusySessions((prev) => prev.filter((s) => s.sessionId !== msg.sessionId));
        if (autoRetryAfterClose) {
          autoRetryAfterClose = false;
          setBusySessionsOpen(false);
          void connect();
        }
      } else {
        setBusySessionsError(msg.error ?? "Échec de libération de la session.");
      }
    },
  );

  // Drapeau non réactif : si true, une fermeture réussie déclenche un retry RDP.
  // Mis à true quand la modale s'ouvre suite à un rdp:error (arbitrage), false
  // quand elle est ouverte manuellement depuis la barre d'outils.
  let autoRetryAfterClose = false;

  /** Demande la liste des sessions TS occupantes à l'agent. */
  function fetchBusySessions(autoRetry: boolean) {
    autoRetryAfterClose = autoRetry;
    setBusySessionsOpen(true);
    setBusySessionsLoading(true);
    setBusySessionsError(null);
    relay.send({ type: "rdp:list_sessions", requestId: crypto.randomUUID() });
  }

  /**
   * Demande à l'agent de libérer une session (disconnect réversible ou logoff
   * destructif). Le logoff exige une confirmation opérateur explicite
   * (useConfirm danger) — l'agent la valide côté serveur via userConfirmedAt.
   */
  async function closeBusySession(session: BusySession, action: "disconnect" | "logoff") {
    if (action === "logoff") {
      const label = session.user ? `${session.domain}\\${session.user}` : `session ${session.sessionId}`;
      const ok = await confirm.confirm({
        title: "Fermer la session RDP",
        body: `Cette action TERMINE la session de ${label} (logoff définitif). Tout travail non sauvegardé sera perdu. Confirmer ?`,
        level: "danger",
        entityName: label,
        confirmLabel: "Fermer définitivement",
      });
      if (!ok) return;
    }
    setClosingSessionId(session.sessionId);
    // userConfirmedAt : contrat serveur — un timestamp ISO présent signifie que
    // l'opérateur a explicitement confirmé une action destructive côté UI.
    relay.send({
      type: "rdp:close_session",
      requestId: crypto.randomUUID(),
      sessionId: session.sessionId,
      action,
      ...(action === "logoff" ? { userConfirmedAt: new Date().toISOString() } : {}),
    });
  }

  // eslint-disable-next-line solid/reactivity
  const offRelayDisconnect = relay.onDisconnect((reason) => {
    if (!tunnelId && status() !== "connected" && status() !== "connecting" && status() !== "loading") return;
    setError(reason);
    setStatus("error");
    teardown(false);
  });

  // ── Connexion ───────────────────────────────────────────────────────────────

  async function connect() {
    if (status() === "connecting" || status() === "connected" || status() === "loading") return;
    setError(null);

    const target = splitRdpHostPort(host(), port());
    const targetHost = target.host.trim();
    const targetPort = validRdpPort(target.port);

    if (!targetHost) {
      setError("Renseignez l'hôte RDP (ex: 10.0.0.5).");
      return;
    }
    if (targetPort == null) {
      setError("Port RDP invalide (1-65535).");
      return;
    }
    if (!canvasEl) {
      setError("Canvas indisponible.");
      return;
    }

    // Credentials : consommés depuis <CredentialInput decryptMode>.
    // En mode VOLT, rdpCredResult().password est déjà déchiffré côté navigateur.
    // En mode manuel, c'est la saisie opérateur.
    const cred = rdpCredResult();
    const ok = cred.ready || (cred.mode === "volt" && cred.credentialId);
    if (!ok) {
      setError("Renseignez les identifiants Windows (ou sélectionnez un credential VOLT).");
      setStatus("error");
      return;
    }
    const rdpUsername = cred.username ?? "";
    const rdpDomain = cred.domain ?? "";
    let rdpPassword = cred.password ?? "";
    if (!rdpPassword) {
      setError("Mot de passe requis (le déchiffrement VOLT a peut-être échoué).");
      setStatus("error");
      return;
    }

    // 1) Prépare le relais et le module WASM avant d'ouvrir le tunnel agent.
    setStatus("loading");
    try {
      await relay.ready();
    } catch (e) {
      rdpPassword = "";
      setError(e instanceof Error ? e.message : String(e));
      setStatus("error");
      return;
    }

    try {
      if (!iron) {
        // Capture des `console.*` AVANT toute initialisation IronRDP afin que
        // chaque log `tracing::*` du WASM (routé vers `console` via
        // `tracing-web::MakeConsoleWriter`) atterrisse aussi dans le buffer
        // visible du panneau.
        installConsoleCapture();
        iron = await loadIronRdp();
        await iron.default(); // initialise le module WASM
        // Niveau de log interne IronRDP au max — visible dans le panneau de
        // debug ci-dessous. On peut redescendre à "warn" une fois le mode
        // DIRECT validé bout-en-bout.
        iron.setup("debug");
      }
    } catch (e) {
      setError(
        "Module RDP indisponible. " +
          "Le binaire WASM doit être déployé avec la console. " +
          `Détail : ${e instanceof Error ? e.message : String(e)}`,
      );
      setStatus("error");
      rdpPassword = "";
      return;
    }

    // 2) Installe le shim de transport : intercepte la WebSocket du WASM et la
    //    route vers le canal `rdp:data` du relais.
    setStatus("connecting");
    tunnelId = crypto.randomUUID();
    tunnelOpenSent = false;
    tunnelCloseHandled = false;
    lastAgentRdpError = null;
    const activeTunnelId = tunnelId;
    const tunnel = installTunnelSocket({
      tunnelId,
      proxyMarker: PROXY_MARKER,
      relaySend: (msg) => sendTunnelControl(msg as RdpJsonMessage),
      relaySendBinary: (frame) => relay.sendBinary(frame),
    });
    restoreTunnelSocket = tunnel.restore;

    try {
      relay.send({ type: "rdp:open", tunnelId, targetHost, targetPort, rdpUsername, rdpDomain });
      tunnelOpenSent = true;
    } catch (e) {
      tunnel.restore();
      if (restoreTunnelSocket === tunnel.restore) restoreTunnelSocket = null;
      rdpPassword = "";
      setError(`Échec d'ouverture du tunnel RDP : ${e instanceof Error ? e.message : String(e)}`);
      setStatus("error");
      teardown(true, "open-failed");
      return;
    }

    // Le tunnel relais↔agent est demandé ; on considère le socket WASM ouvert
    // dès que le WASM l'instancie. (Le relais peut envoyer un `rdp:data`
    // d'établissement aussitôt — le shim le bufferise via feed().)
    tunnel.waitForSocket
      .then((sock) => {
        if (tunnelId !== activeTunnelId || sock.url !== tunnel.proxyUrl) return;
        tunnelSocket = sock;
        sock.markOpen();
      })
      .catch((e) => {
        if (tunnelId !== activeTunnelId) return;
        tunnel.restore();
        if (restoreTunnelSocket === tunnel.restore) restoreTunnelSocket = null;
        setError(e instanceof Error ? e.message : String(e));
        setStatus("error");
        teardown(true, "wasm-socket-timeout");
      });

    // 3) Construit et lance la session RDP.
    try {
      const initialSize = desiredDesktopSize();
      // On ne set PAS canvas.width/height ici : le WASM le fait via
      // `builder.renderCanvas` à l'établissement de la session.

      const builder = new iron.SessionBuilder();
      builder.username(rdpUsername);
      builder.password(rdpPassword);
      if (rdpDomain.trim()) builder.serverDomain(rdpDomain.trim());
      rdpPassword = "";
      builder.destination(rdpDestination(targetHost, targetPort));
      // proxyAddress : URL marquée → interceptée par le shim, jamais ouverte en direct.
      builder.proxyAddress(tunnel.proxyUrl);
      builder.authToken("none");
      builder.desktopSize(new iron.DesktopSize(initialSize.width, initialSize.height));
      builder.renderCanvas(canvasEl);
      // ── LE FIX DU RESIZE ────────────────────────────────────────────────
      // Active le canal Display Control (MS-RDPEDISP). Sans cette extension,
      // `session.resize()` appelle `encode_resize` qui retourne None
      // ("Display Control Virtual Channel is not available") et le serveur ne
      // reçoit JAMAIS la demande de desktop resize → toute la chaîne de resize
      // (fenêtre, plein écran, détachement) est silencieusement morte.
      builder.extension(new iron.Extension("display_control", true));
      builder.extension(new iron.Extension("enable_credssp", true));
      // Mode DIRECT : le tuyau `rdp:data` transporte le RDP brut (pas de proxy
      // RDCleanPath). Le client WASM fait lui-même X.224 + TLS + CredSSP.
      builder.extension(new iron.Extension("use_rdcleanpath", false));
      // 32 bpp explicite : route les bitmaps via le chemin RDP 6.0 (mature)
      // au lieu de l'Interleaved-RLE 16 bpp (historiquement source d'edge
      // cases et de spam debug `Non-32 bpp compressed RLE_BITMAP_STREAM`).
      // C'est aussi le défaut du builder ; on le rend explicite ici.
      builder.extension(new iron.Extension("color_depth", 32));
      if (audioEnabled()) {
        builder.extension(new iron.Extension("enable_audio_playback", true));
      }
      // Les callbacks clipboard sont TOUJOURS enregistrés : ils interrogent le
      // signal `clipboardMode()` au moment de l'appel, ce qui permet à
      // l'opérateur de changer de mode en pleine session sans se reconnecter.
      // `forceClipboardUpdateCallback` sert aussi de SIGNAL pour la fin du
      // handshake CLIPRDR : le serveur l'invoque quand il réclame le contenu
      // (FormatDataRequest), preuve que le FormatList a été reçu. On marque
      // l'instant pour que `waitForClipboardHandshake` sache que le Ctrl+V peut
      // bientôt partir.

      builder.forceClipboardUpdateCallback(async () => {
        clipboardServerSignalEpoch++;
        clipboardServerSignalAtMs = performance.now();
        if (!clipboardAllowsLocalToRemote(clipboardMode())) return new iron!.ClipboardData();
        return (await readBrowserClipboardData()) ?? new iron!.ClipboardData();
      });

      builder.remoteClipboardChangedCallback((content: IronClipboardData) => {
        if (!clipboardAllowsRemoteToLocal(clipboardMode())) return;
        // Alimente l'historique du moteur partagé avant l'écriture navigateur.
        const snapshot: ClipboardSnapshot = {};
        for (const item of content.items()) {
          const mime = item.mimeType();
          const value = item.value();
          if (mime === "text/plain" && typeof value === "string") snapshot.text = value;
          else if (mime === "text/html" && typeof value === "string") snapshot.html = value;
          else if (mime === "image/png" && value instanceof Uint8Array) snapshot.png = value;
        }
        clipboardEngine.onRemoteChanged(snapshot, async (s) => {
          // Réutilise la fonction d'écriture navigateur existante.
          const ironData = new iron!.ClipboardData();
          if (s.text) ironData.addText("text/plain", s.text);
          if (s.html) ironData.addText("text/html", s.html);
          if (s.png) ironData.addBinary("image/png", s.png);
          await writeBrowserClipboardData(ironData);
        });
      });
      builder.setCursorStyleCallbackContext(surfaceEl);
      builder.setCursorStyleCallback((style: string) => {
        if (surfaceEl) surfaceEl.style.cursor = style || "default";
      });

      session = await builder.connect();
      // La connexion WASM est établie : l'interception globale n'est plus utile.
      tunnel.restore();
      if (restoreTunnelSocket === tunnel.restore) restoreTunnelSocket = null;

      // Taille réellement négociée par le serveur. Le WASM a déjà réglé le
      // backing store du canvas (via renderCanvas à l'init) ; on NE le réécrit
      // pas ici. `lastDesktopResize` est initialisé à la taille confirmée pour
      // que le premier `resizeRdpDesktop` ne redemande pas la même taille.
      const ds = session.desktopSize();
      lastDesktopResize = { width: ds.width, height: ds.height };
      setStatus("connected");
      setBarVisible(true);
      armBarIdleTimer();
      surfaceEl?.focus();
      // Synchronise l'état des touches de verrouillage avec l'opérateur dès la
      // connexion. Sans ça, si l'hôte a CapsLock actif mais l'opérateur non
      // (ou inverse), l'état LED est désynchronisé → majuscules inversées.
      // `getModifierState` lit l'état navigateur (reflète le clavier opérateur).
      try {
        const ev = window;
        const caps = typeof KeyboardEvent !== "undefined" && new KeyboardEvent("keydown").getModifierState("CapsLock");
        const num = typeof KeyboardEvent !== "undefined" && new KeyboardEvent("keydown").getModifierState("NumLock");
        const scroll =
          typeof KeyboardEvent !== "undefined" && new KeyboardEvent("keydown").getModifierState("ScrollLock");
        void ev;
        session?.synchronizeLockKeys(Boolean(scroll), Boolean(num), Boolean(caps), false);
      } catch {
        /* synchronizeLockKeys best-effort — pas critique si indispo. */
      }
      requestAnimationFrame(() => {
        reclampToolbarPosition(true);
        scheduleSettledSurfaceResize(true);
      });

      // Boucle de session — résout à la fin du RDP (déconnexion serveur, etc.).
      session
        .run()
        .then((info) => {
          finishSession(info.reason ? `Session terminée : ${info.reason()}` : null);
        })
        .catch((e) => {
          setError(`Erreur de session RDP : ${describeIronError(e)}`);
          setStatus("error");
          teardown(true, "session-error");
        });
    } catch (e) {
      tunnel.restore();
      if (restoreTunnelSocket === tunnel.restore) restoreTunnelSocket = null;
      rdpPassword = "";
      setError(lastAgentRdpError ?? `Échec de connexion RDP : ${describeIronError(e)}`);
      setStatus("error");
      teardown(true, "connect-failed");
    }
  }

  /** Fin de session « propre » (déconnexion serveur ou agent). */
  function finishSession(message: string | null, sendClose = true) {
    if (message) setError(message);
    setStatus("idle");
    teardown(sendClose, "session-ended");
  }

  /**
   * Démontage complet : shutdown du client IronRDP, fermeture du tunnel relais,
   * remise à zéro de l'état. `sendClose` évite un double `rdp:close` si le
   * relais a déjà signalé la fermeture.
   */
  function teardown(sendClose: boolean, reason = "operator-disconnect") {
    if (teardownInProgress) {
      if (sendClose) sendTunnelCloseOnce(reason);
      else tunnelCloseHandled = true;
      return;
    }

    teardownInProgress = true;
    try {
      if (sendClose) sendTunnelCloseOnce(reason);
      else tunnelCloseHandled = true;

      if (restoreTunnelSocket) {
        try {
          restoreTunnelSocket();
        } catch {
          /* ignore */
        }
        restoreTunnelSocket = null;
      }

      if (session) {
        try {
          session.releaseAllInputs();
        } catch {
          /* ignore */
        }
        try {
          session.shutdown();
        } catch {
          /* ignore */
        }
        session = null;
      }
      if (tunnelSocket) {
        tunnelSocket.failOrClose(1000, "panel teardown");
        tunnelSocket = null;
      }
      tunnelId = null;
      tunnelOpenSent = false;
      tunnelCloseHandled = false;
      lastDesktopResize = null;
      resizeGeneration++;
      clearSettledResizeTimers();
      // Réinitialise l'état du handshake CLIPRDR : un signal FormatDataRequest
      // d'une session précédente ne doit pas être interprété comme la fin du
      // handshake d'un paste d'une nouvelle session.
      clipboardServerSignalEpoch = 0;
      clipboardServerSignalAtMs = 0;
      clipboardPasteEpoch = 0;
      clipboardPasteInFlight = false;
      clipboardServerSignalAtStart = 0;
    } finally {
      teardownInProgress = false;
    }
  }

  function disconnect() {
    teardown(true, "operator-disconnect");
    setStatus("idle");
  }

  // ── Injection clavier / souris vers le client IronRDP ───────────────────────
  // À la différence de video.tsx (input → WS), l'input va ici directement au
  // client RDP WASM via `session.applyInputs(InputTransaction)`.

  /** Applique un unique DeviceEvent dans une transaction. */
  function applyEvent(make: () => IronDeviceEvent) {
    if (!session || !iron) return;
    try {
      const tx = new iron.InputTransaction();
      tx.addEvent(make());
      session.applyInputs(tx);
    } catch (err) {
      console.error("[rdp] applyInputs:", err);
    }
  }

  function releaseRemoteInputs(clearSuppressed = true) {
    if (clearSuppressed) {
      suppressedKeyUps.clear();
      unicodePressedCodes.clear();
    }
    if (!session) return;
    try {
      session.releaseAllInputs();
    } catch {
      /* ignore */
    }
  }

  /** Convertit les coordonnées écran en coordonnées canvas (pixels du bureau). */
  function canvasCoords(e: MouseEvent): { x: number; y: number } {
    if (!canvasEl) return { x: 0, y: 0 };
    const rect = canvasContentRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return { x: 0, y: 0 };
    const sx = canvasEl.width / rect.width;
    const sy = canvasEl.height / rect.height;
    return {
      x: clampRdpSize(Math.round((e.clientX - rect.left) * sx), 0, canvasEl.width),
      y: clampRdpSize(Math.round((e.clientY - rect.top) * sy), 0, canvasEl.height),
    };
  }

  function onMouseMove(e: MouseEvent) {
    wakeBar();
    if (status() !== "connected" || !iron) return;
    const { x, y } = canvasCoords(e);
    applyEvent(() => iron!.DeviceEvent.mouseMove(x, y));
  }

  function onMouseDown(e: MouseEvent) {
    if (status() !== "connected" || !iron) return;
    e.preventDefault();
    surfaceEl?.focus();
    applyEvent(() => iron!.DeviceEvent.mouseButtonPressed(e.button));
  }

  function onMouseUp(e: MouseEvent) {
    if (status() !== "connected" || !iron) return;
    e.preventDefault();
    applyEvent(() => iron!.DeviceEvent.mouseButtonReleased(e.button));
  }

  function onWheel(e: WheelEvent) {
    if (status() !== "connected" || !iron) return;
    e.preventDefault();
    // unit = 1 (rotation_unit) ; signe inversé pour respecter le sens RDP.
    if (e.deltaY !== 0) {
      const amount = e.deltaY > 0 ? -1 : 1;
      applyEvent(() => iron!.DeviceEvent.wheelRotations(true, amount, 1));
    }
    if (e.deltaX !== 0) {
      const amount = e.deltaX > 0 ? -1 : 1;
      applyEvent(() => iron!.DeviceEvent.wheelRotations(false, amount, 1));
    }
  }

  /**
   * Décide si on doit envoyer le CARACTÈRE unicode (event.key) plutôt que le
   * scancode (position physique). C'est crucial pour les opérateurs dont le
   * layout diffère du Windows distant (Mac AZERTY → PC QWERTY).
   *
   * Sans ça : l'opérateur tape `-` sur son Mac AZERTY → scancode de la position
   * physique → le Windows QWERTY interprète cette position comme `=`.
   *
   * Règle : on envoie l'unicode pour les caractères imprimables SANS modificateur
   * (ni Ctrl, ni Cmd, ni Alt). Les raccourcis (Ctrl+C) et les touches spéciales
   * (F1, flèches, Enter) gardent le scancode. IronRDP envoie alors un événement
   * UnicodeKeyboardEvent que Windows interprète comme le caractère réel, pas la
   * position — le layout du Windows distant est contourné.
   *
   * On ignore aussi les événements de composition IME (isComposing) : le caractère
   * final arrivera via un keydown ultérieur quand la composition sera résolue.
   */
  function toUnicodeChar(e: KeyboardEvent): string | null {
    if (!unicodeMode()) return null; // Toggle OFF → scancodes (PowerShell-compatible).
    if (e.isComposing) return null;
    if (e.ctrlKey || e.metaKey || e.altKey || e.getModifierState?.("AltGraph")) return null;
    if (e.key.length !== 1) return null; // caractères multi-points (emojis) exclus
    const ch = e.key;
    if (ch < " ") return null; // caractères de contrôle (Backspace, Tab, Enter...)
    if (ch === " " || ch === "\u00a0") return null; // Space garde le scancode
    return ch;
  }

  function onKeyDown(e: KeyboardEvent) {
    if (status() !== "connected" || !iron) return;
    if (clipboardAllowsLocalToRemote(clipboardMode()) && isLocalPasteShortcut(e)) {
      e.preventDefault();
      // On AVALE le Ctrl+V/Cmd+V : ni le scancode Ctrl, ni le scancode V ne sont
      // envoyés tant que le transfert clipboard est en cours. `suppressedKeyUps`
      // empêche le keyup correspondant de fuiter (sinon l'état modifieur serait
      // désynchronisé). La libération se fait dans le `finally` du paste.
      suppressedKeyUps.add("ControlLeft");
      suppressedKeyUps.add("ControlRight");
      suppressedKeyUps.add("MetaLeft");
      suppressedKeyUps.add("MetaRight");
      suppressedKeyUps.add("KeyV");
      void pasteLocalClipboardIntoRemote().then((result) => {
        // Fallback natif : si la sync clipboard a échoué (permission refusée,
        // handshake mort, lecture vide mais on veut quand même coller), on envoie
        // le scancode Ctrl+V au Windows distant qui collera son PROPRE presse-papier.
        // Sans ça, l'opérateur voit "Ctrl+V ne fait rien" en cas d'échec silencieux.
        if (result.kind === "native-fallback") sendRemoteCtrlV();
      });
      return;
    }
    // ── Chemin Unicode (caractères imprimables sans modificateur) ──
    // On envoie le caractère RÉEL (event.key) plutôt que la position physique
    // (scancode). C'est ce qui corrige les mismatches de layout : un opérateur
    // Mac AZERTY qui tape `-` envoie bien `-` au Windows QWERTY distant, pas `=`.
    // IronRDP traduit unicodePressed → UnicodeKeyboardEvent (MS-RDPBCGR §2.2.8.1.1.3.1.1.1).
    const unicode = toUnicodeChar(e);
    if (unicode !== null) {
      e.preventDefault();
      // On stocke le caractère exact pour le keyup : `toUnicodeChar` sur le
      // keyup pourrait retourner null (modifiers changés, ex. Shift relâché
      // avant la lettre), ce qui empêcherait le `unicodeReleased`.
      unicodePressedCodes.set(e.code, unicode);
      suppressedKeyUps.add(e.code);
      applyEvent(() => iron!.DeviceEvent.unicodePressed(unicode));
      return;
    }

    // Évaluation du profil clavier (remplace l'ancien shouldSuppressMetaKey).
    // Le profil `mac` (cmdKeyMode="local") traduit Cmd→Ctrl pour les raccourcis
    // usuels (Cmd+C = Ctrl+C) et garde locales les Cmd+Tab/Space/Q/H.
    // Le profil `windows` (cmdKeyMode="windows") envoie Cmd comme touche Windows.
    const action = resolveKey(keyboardProfile(), e);
    if (action.kind === "local") {
      // Touche gardée locale (Cmd+Tab switch d'app, etc.) : on l'avale et on
      // relâche les inputs distants pour éviter un état clavier fantôme.
      e.preventDefault();
      suppressedKeyUps.add(e.code);
      releaseRemoteInputs(false);
      return;
    }
    if (action.kind === "ignore") {
      e.preventDefault();
      return;
    }
    // action.kind === "map" : on envoie le scancode traduit.
    // Pour Cmd (MetaLeft/Right) en profil mac, `resolveKey` renvoie le scancode
    // de Control (0x1d) — le Windows distant voit donc un Ctrl down. Les
    // Cmd+lettre suivants produiront bien Ctrl+lettre côté Windows.
    if (action.scancode === null) return;
    const sc = action.scancode;
    e.preventDefault();
    applyEvent(() => iron!.DeviceEvent.keyPressed(sc));
  }

  function onKeyUp(e: KeyboardEvent) {
    if (status() !== "connected" || !iron) return;
    // Relâche le caractère unicode stocké au keydown correspondant. Sans cet
    // appel, le codepoint reste "enfoncé" côté Windows → wedge les terminaux.
    const pressedUnicode = unicodePressedCodes.get(e.code);
    if (pressedUnicode !== undefined) {
      unicodePressedCodes.delete(e.code);
      suppressedKeyUps.delete(e.code);
      e.preventDefault();
      applyEvent(() => iron!.DeviceEvent.unicodeReleased(pressedUnicode));
      return;
    }
    if (suppressedKeyUps.delete(e.code)) {
      e.preventDefault();
      return;
    }
    const action = resolveKey(keyboardProfile(), e);
    if (action.kind === "local" || action.kind === "ignore") {
      e.preventDefault();
      return;
    }
    // action.kind === "map" : on relâche le scancode traduit. Même logique que
    // keydown : Cmd (MetaLeft/Right) en profil mac relâche le scancode de Control.
    if (action.scancode === null) return;
    const sc = action.scancode;
    e.preventDefault();
    applyEvent(() => iron!.DeviceEvent.keyReleased(sc));
  }

  function onContextMenu(e: Event) {
    if (status() === "connected") e.preventDefault();
  }

  /**
   * Empêche le navigateur d'écraser le presse-papier local pendant un Ctrl+C/Ctrl+X.
   *
   * Le scancode Ctrl+C/Cmd+C a déjà été envoyé au Windows distant (via onKeyDown →
   * resolveKey). Le Windows distant copie puis IronRDP nous notifie via
   * `remoteClipboardChangedCallback` → `writeBrowserClipboardData` écrit alors le
   * contenu distant dans le clipboard local. Ce callback est asynchrone (latence
   * réseau + cycle CLIPRDR), donc si on laissait le navigateur faire son `copy`
   * par défaut, il écrirait une sélection locale (souvent vide) par-dessus le
   * contenu distant en cours d'arrivée → l'opérateur colle du vide en Cmd+Tab.
   *
   * `preventDefault` neutralise ce risque.
   */
  function onSurfaceCopy(e: ClipboardEvent) {
    if (status() === "connected" && clipboardAllowsRemoteToLocal(clipboardMode())) e.preventDefault();
  }

  function onSurfaceBlur() {
    // Ne pas relâcher les inputs si un paste clipboard est en cours : le
    // handshake CLIPRDR + le Ctrl+V synthétisé ont besoin d'un état clavier
    // intact. Le `finally` du paste débloque `clipboardPasteInFlight`.
    if (clipboardPasteInFlight) return;
    releaseRemoteInputs();
  }

  function onDocumentVisibilityChange() {
    if (document.hidden && !clipboardPasteInFlight) releaseRemoteInputs();
  }

  function onWindowBlur() {
    if (clipboardPasteInFlight) return;
    releaseRemoteInputs();
  }

  // ── Redimensionnement — recale la session sur la taille de la surface ───────
  let resizeObs: ResizeObserver | null = null;
  let resizeDebounce: ReturnType<typeof setTimeout> | null = null;
  // Écoute du changement de devicePixelRatio : le panneau calcule la résolution
  // RDP en pixels physiques (CSS × dpr), donc un zoom navigateur (Ctrl/Cmd + ±)
  // ou un déplacement entre écrans de densités différentes doit déclencher un
  // recal. `matchMedia("(resolution: Ndppx)")` ne se déclenche qu'une fois, on
  // le ré-arme à chaque changement (même pattern que video.tsx::armDprListener).
  let dprMql: MediaQueryList | null = null;

  function onDprChange() {
    handleResize();
    armDprListener();
  }

  function armDprListener() {
    if (dprMql) {
      dprMql.removeEventListener?.("change", onDprChange);
      dprMql = null;
    }
    const dpr = window.devicePixelRatio || 1;
    try {
      dprMql = window.matchMedia(`(resolution: ${dpr}dppx)`);
      dprMql.addEventListener?.("change", onDprChange);
    } catch {
      dprMql = null;
    }
  }

  function handleResize() {
    if (status() !== "connected" || !session || !surfaceEl) return;
    reclampToolbarPosition(true);
    if (resizeDebounce) clearTimeout(resizeDebounce);
    resizeDebounce = setTimeout(() => {
      applySurfaceResize(false);
    }, RDP_RESIZE_DEBOUNCE_MS);
  }

  function refreshVisibleSurface() {
    if (props.visible === false || status() !== "connected" || !session || !surfaceEl || !canvasEl) return;
    surfaceEl.focus();
    setBarVisible(true);
    scheduleSettledSurfaceResize(true);
  }

  function syncFullscreenState() {
    const mainFull = Boolean(document.fullscreenElement);
    let popoutFull = false;
    try {
      popoutFull = Boolean(detachWindow && !detachWindow.closed && detachWindow.document.fullscreenElement);
    } catch {
      popoutFull = false;
    }
    setIsFullscreen(mainFull || popoutFull);
    scheduleSettledSurfaceResize(true);
    surfaceEl?.focus();
  }

  function toggleFullscreen() {
    const doc = stageEl?.ownerDocument ?? document;
    if (doc.fullscreenElement) {
      void doc.exitFullscreen();
      return;
    }
    if (stageEl?.requestFullscreen) {
      void stageEl.requestFullscreen().then(syncFullscreenState, () => syncFullscreenState());
    }
  }

  function copyPopupStyles(doc: Document) {
    for (const node of Array.from(document.head.querySelectorAll('link[rel="stylesheet"], style'))) {
      doc.head.appendChild(node.cloneNode(true));
    }
    const style = doc.createElement("style");
    style.textContent = `
      html, body, #rdp-popout-root { width: 100%; height: 100%; margin: 0; overflow: hidden; background: #0a0a0a; }
      body { color: #e5e7eb; font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
      #rdp-popout-root { display: flex; min-height: 0; }
      .rdp-stage { flex: 1 1 auto; }
    `;
    doc.head.appendChild(style);
  }

  function reattachStageFromPopout() {
    if (detachPoll) {
      clearInterval(detachPoll);
      detachPoll = null;
    }
    const w = detachWindow;
    detachWindow = null;
    if (stageEl && stageHomeEl && stageEl.parentNode !== stageHomeEl) {
      stageHomeEl.appendChild(stageEl);
    }
    setDetached(false);
    syncFullscreenState();
    refreshVisibleSurface();
    try {
      if (w && !w.closed) w.close();
    } catch {
      /* ignore */
    }
  }

  function detachLiveStage() {
    if (!stageEl || detached()) {
      detachWindow?.focus();
      return;
    }
    const w = window.open("", "rws-rdp-live-popout", "width=1280,height=800,popup=1");
    if (!w) {
      setError("Le navigateur a bloqué la fenêtre détachée.");
      return;
    }

    const doc = w.document;
    doc.open();
    doc.write(
      '<!doctype html><html><head><title>RemoteWS RDP</title></head><body><div id="rdp-popout-root"></div></body></html>',
    );
    doc.close();
    copyPopupStyles(doc);
    const root = doc.getElementById("rdp-popout-root");
    if (!root) {
      w.close();
      setError("Fenêtre détachée RDP indisponible.");
      return;
    }

    detachWindow = w;
    root.appendChild(stageEl);
    setDetached(true);
    w.addEventListener("pagehide", reattachStageFromPopout, { once: true });
    doc.addEventListener("fullscreenchange", syncFullscreenState);
    detachPoll = setInterval(() => {
      if (!detachWindow || detachWindow.closed) reattachStageFromPopout();
    }, 800);
    w.focus();
    requestAnimationFrame(() => {
      reclampToolbarPosition(true);
      refreshVisibleSurface();
    });
  }

  let wasVisible = false;
  createEffect(() => {
    const nowVisible = props.visible !== false;
    if (nowVisible && !wasVisible) refreshVisibleSurface();
    wasVisible = nowVisible;
  });

  // Abonnement au buffer de logs IronRDP : à chaque nouveau log, on bump le
  // signal `logsVersion` pour forcer la liste `<For>` à se re-rendre.
  const logsUnsub = () => {
    let v = 0;
    const cb = () => setLogsVersion(++v);
    ironLogSubscribers.add(cb);
    return () => ironLogSubscribers.delete(cb);
  };
  let unsubLogs: (() => void) | null = null;

  onMount(() => {
    restoreToolbarPosition();
    requestAnimationFrame(() => reclampToolbarPosition(true));
    if (surfaceEl) {
      resizeObs = new ResizeObserver(handleResize);
      resizeObs.observe(surfaceEl);
    }
    window.addEventListener("resize", handleResize);
    armDprListener();
    document.addEventListener("fullscreenchange", syncFullscreenState);
    document.addEventListener("visibilitychange", onDocumentVisibilityChange);
    window.addEventListener("blur", onWindowBlur);
    unsubLogs = logsUnsub();
  });

  // ── Démontage propre du panneau ─────────────────────────────────────────────
  onCleanup(() => {
    offData();
    offClose();
    offError();
    offSessions();
    offSessionClosed();
    offRelayDisconnect();
    if (barIdleTimer) clearTimeout(barIdleTimer);
    if (resizeDebounce) clearTimeout(resizeDebounce);
    if (detachPoll) clearInterval(detachPoll);
    reattachStageFromPopout();
    resizeObs?.disconnect();
    window.removeEventListener("resize", handleResize);
    if (dprMql) {
      dprMql.removeEventListener?.("change", onDprChange);
      dprMql = null;
    }
    document.removeEventListener("fullscreenchange", syncFullscreenState);
    document.removeEventListener("visibilitychange", onDocumentVisibilityChange);
    window.removeEventListener("blur", onWindowBlur);
    unsubLogs?.();
    // Envoi de `rdp:close` au relais si une session est encore active.
    teardown(true, "panel-unmount");
    relay.close("panel-unmount");
  });

  // ── Actions sur les logs ────────────────────────────────────────────────────
  function copyLogs() {
    // On lit `logsVersion()` pour s'assurer de prendre un snapshot cohérent
    // (la voie réactive est inutile ici, mais elle marque la dépendance pour
    // toute future ré-évaluation et garantit qu'on est à jour à la frame).
    void logsVersion();
    const text = ironLogBuffer
      .map((l) => {
        const ts = new Date(l.t).toISOString().slice(11, 23); // HH:MM:SS.mmm
        return `[${ts}] ${l.level.toUpperCase().padEnd(5)} ${l.line}`;
      })
      .join("\n");
    void navigator.clipboard?.writeText(text);
  }
  function clearLogs() {
    ironLogBuffer.length = 0;
    setLogsVersion((v) => v + 1);
  }

  const statusLabel = () => {
    switch (status()) {
      case "loading":
        return "Chargement du module RDP…";
      case "connecting":
        return "Connexion RDP…";
      case "connected":
        return "Connecté";
      case "error":
        return "Erreur";
      default:
        return "Déconnecté";
    }
  };

  const isActive = () => status() === "connected" || status() === "connecting" || status() === "loading";

  return (
    <div class="rdp-wrap">
      <div ref={(el) => (stageHomeEl = el)} class="rdp-stage-home">
        <div
          ref={(el) => (stageEl = el)}
          class="rdp-stage"
          classList={{
            "rdp-stage--fullscreen": isFullscreen(),
          }}
        >
          {/* Surface interactive + canvas RDP */}
          <div
            ref={(el) => (surfaceEl = el)}
            class="rdp-surface"
            classList={{
              "rdp-surface--active": status() === "connected",
            }}
            tabIndex={status() === "connected" ? 0 : undefined}
            onMouseMove={onMouseMove}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onWheel={onWheel}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            onBlur={onSurfaceBlur}
            onPaste={(e) => void pasteEventIntoRemote(e)}
            onCopy={onSurfaceCopy}
            onCut={onSurfaceCopy}
            onContextMenu={onContextMenu}
          >
            <canvas
              ref={(el) => (canvasEl = el)}
              class="rdp-canvas"
              width={1280}
              height={720}
              style={{
                display: isActive() ? "block" : "none",
              }}
            />

            {/* Vue de connexion — formulaire de credentials Windows */}
            <Show when={status() === "idle" || status() === "error"}>
              <div class="rdp-connect-view">
                <span class="rdp-connect-title">Connexion RDP</span>
                <p class="rdp-connect-hint">
                  Ouvre le bureau Windows natif de l'hôte via le protocole RDP. Le client RDP s'exécute dans le
                  navigateur ; les identifiants ne sont transmis qu'au client local et ne sont jamais journalisés.
                </p>

                <Show when={!featureRdp()}>
                  <p class="rdp-connect-hint" style={{ color: "#fbbf24" }}>
                    L'agent de cet hôte n'annonce pas la capacité RDP — la connexion peut échouer.
                  </p>
                </Show>

                <div class="rdp-mode-switch" role="group" aria-label="Qualité RDP">
                  <label class="rdp-field rdp-field--inline">
                    <span class="rdp-field-label">Qualité</span>
                    <select
                      class="rdp-field-input"
                      value={qualityProfile()}
                      onChange={(e) => setQualityProfileValue(e.currentTarget.value as RdpQualityProfile)}
                    >
                      <option value="eco">Éco</option>
                      <option value="standard">Standard</option>
                      <option value="high">Haute</option>
                      <option value="native">Native</option>
                    </select>
                  </label>
                  <span class="rdp-mode-hint">La résolution RDP suit toujours la surface visible.</span>
                </div>

                <label class="rdp-field">
                  <span class="rdp-field-label">IP / hôte RDP</span>
                  <input
                    class="rdp-field-input"
                    type="text"
                    placeholder="10.0.0.5"
                    value={host()}
                    onInput={(e) => setHost(e.currentTarget.value)}
                  />
                </label>
                <label class="rdp-field">
                  <span class="rdp-field-label">Port</span>
                  <input
                    class="rdp-field-input"
                    type="number"
                    inputmode="numeric"
                    min="1"
                    max="65535"
                    placeholder="3389"
                    value={port()}
                    onInput={(e) => setPort(e.currentTarget.value)}
                  />
                </label>
                <CredentialInput
                  usage="rdp"
                  vaultDefaults={
                    props.vaultDefaults ?? (props.defaultCredentialId ? { rdp: props.defaultCredentialId } : undefined)
                  }
                  layout="stacked"
                  fields={{ username: true, password: true, domain: "optional" }}
                  defaults={{
                    username: props.defaultUsername,
                    domain: props.defaultDomain,
                  }}
                  decryptMode={true}
                  decryptParams={{ sessionCode: props.sessionCode ?? undefined }}
                  disabled={status() === "connecting" || status() === "loading"}
                  // eslint-disable-next-line solid/reactivity -- stub non utilisé en decryptMode
                  requestAgentKey={async () => {
                    throw new Error("requestAgentKey n'est pas utilisé en decryptMode");
                  }}
                  onResult={(r) => setRdpCredResult(r)}
                />

                <label class="rdp-field">
                  <span class="rdp-field-label">Presse-papier</span>
                  <select
                    class="rdp-field-input"
                    value={clipboardMode()}
                    disabled={status() === "connecting" || status() === "loading"}
                    title="Modifiable en pleine session : le transfert s'applique immédiatement."
                    onChange={(e) => setClipboardMode(e.currentTarget.value as RdpClipboardMode)}
                  >
                    <option value="none">Aucun</option>
                    <option value="local-to-remote">Opérateur → remote</option>
                    <option value="remote-to-local">Remote → opérateur</option>
                    <option value="bidirectional">Bidirectionnel</option>
                  </select>
                </label>

                <label class="rdp-check">
                  <input
                    type="checkbox"
                    checked={audioEnabled()}
                    disabled={status() === "connected" || status() === "connecting" || status() === "loading"}
                    onChange={(e) => setAudioEnabled(e.currentTarget.checked)}
                  />
                  <span>Audio vers navigateur</span>
                </label>

                <Show when={error()}>
                  <div class="rdp-error">{error()}</div>
                </Show>

                <Show when={error()}>
                  <button
                    class="btn btn--sm"
                    title="Lister les sessions Windows occupant le host et en libérer une"
                    onClick={() => fetchBusySessions(false)}
                  >
                    Sessions RDP occupées…
                  </button>
                </Show>

                <button class="btn btn--primary" onClick={() => void connect()}>
                  Connecter RDP
                </button>
              </div>
            </Show>
          </div>

          {/* Modale : sessions TS occupantes (libération avant retry RDP) — rendue
              au niveau racine du stage pour s'afficher quel que soit le status
              (idle/error OU connecté via le bouton "Sessions" de la barre). */}
          <Show when={busySessionsOpen()}>
            <div class="rdp-busy-overlay" role="dialog" aria-modal="true">
              <div class="rdp-busy-modal">
                <div class="rdp-busy-header">
                  <span class="rdp-busy-title">Sessions Windows occupant le host</span>
                  <button class="rdp-busy-close" title="Fermer" onClick={() => setBusySessionsOpen(false)}>
                    ✕
                  </button>
                </div>

                <p class="rdp-busy-hint">
                  Une session RDP existante empêche la connexion (arbitrage Windows).
                  <br />
                  Choisissez une action pour libérer la session, puis réessayez.
                </p>

                <Show when={busySessionsLoading()}>
                  <div class="rdp-busy-loading">Énumération des sessions…</div>
                </Show>

                <Show when={busySessionsError()}>
                  <div class="rdp-error">{busySessionsError()}</div>
                </Show>

                <Show when={!busySessionsLoading() && busySessions().length === 0 && !busySessionsError()}>
                  <div class="rdp-busy-empty">Aucune session interactive occupante détectée.</div>
                </Show>

                <Show when={busySessions().length > 0}>
                  <table class="rdp-busy-table">
                    <thead>
                      <tr>
                        <th>Utilisateur</th>
                        <th>État</th>
                        <th>Session</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={busySessions()}>
                        {(s) => (
                          <tr>
                            <td>
                              <span class="rdp-busy-user">
                                {s.domain ? `${s.domain}\\` : ""}
                                {s.user || "?"}
                              </span>
                              <Show when={s.isConsole}>
                                <span class="rdp-busy-badge" title="Session console physique">
                                  console
                                </span>
                              </Show>
                            </td>
                            <td>
                              <span class={`rdp-busy-state rdp-busy-state--${s.state}`}>{s.state}</span>
                            </td>
                            <td>
                              <span class="rdp-busy-session">{s.sessionName || `#${s.sessionId}`}</span>
                            </td>
                            <td class="rdp-busy-actions">
                              <Show when={props.onCaptureSessionWs}>
                                <button
                                  class="btn btn--sm btn--primary"
                                  title="Capturer cette session en WS (WGC) — bascule la vue en flux WebSocket sur cette session précise"
                                  onClick={() =>
                                    props.onCaptureSessionWs?.(
                                      s.sessionId,
                                      s.user ? `${s.domain ? `${s.domain}\\` : ""}${s.user}` : `#${s.sessionId}`,
                                    )
                                  }
                                >
                                  WS
                                </button>
                              </Show>
                              <button
                                class="btn btn--sm btn--warn"
                                disabled={closingSessionId() === s.sessionId}
                                title="Déconnecter la session (réversible — les programmes restent ouverts)"
                                onClick={() => void closeBusySession(s, "disconnect")}
                              >
                                {closingSessionId() === s.sessionId ? "…" : "Déconnecter"}
                              </button>
                              <button
                                class="btn btn--sm btn--danger"
                                disabled={closingSessionId() === s.sessionId}
                                title="Fermer définitivement la session (logoff — perte du travail non sauvegardé)"
                                onClick={() => void closeBusySession(s, "logoff")}
                              >
                                Fermer
                              </button>
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </Show>

                <div class="rdp-busy-footer">
                  <button class="btn btn--sm" onClick={() => void fetchBusySessions(autoRetryAfterClose)}>
                    Actualiser
                  </button>
                  <button class="btn btn--sm" onClick={() => setBusySessionsOpen(false)}>
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </Show>

          {/* Panneau de debug RDP — capture des logs WASM (console.*) */}
          <Show when={logsVisible()}>
            <div class="rdp-debug">
              <div class="rdp-debug-bar">
                <span class="rdp-debug-title">Logs RDP ({logsSnapshot().length})</span>
                <button class="btn btn--sm" onClick={copyLogs}>
                  Copier
                </button>
                <button class="btn btn--sm" onClick={clearLogs}>
                  Vider
                </button>
                <button class="btn btn--sm" onClick={() => setLogsVisible(false)}>
                  Masquer
                </button>
              </div>
              <pre class="rdp-debug-pre">
                <For each={logsSnapshot()}>
                  {(l) => (
                    <div class={`rdp-debug-line rdp-debug-line--${l.level}`}>
                      <span class="rdp-debug-ts">{new Date(l.t).toISOString().slice(11, 23)}</span>
                      <span class={`rdp-debug-lvl rdp-debug-lvl--${l.level}`}>{l.level.toUpperCase()}</span>
                      <span class="rdp-debug-msg">{l.line}</span>
                    </div>
                  )}
                </For>
              </pre>
            </div>
          </Show>
          <Show when={!logsVisible()}>
            <button class="rdp-debug-toggle btn btn--sm" onClick={() => setLogsVisible(true)}>
              Logs RDP
            </button>
          </Show>

          {/* Barre flottante de contrôle — session active */}
          <Show when={isActive()}>
            <div
              ref={(el) => (toolbarEl = el)}
              class="rdp-bar"
              classList={{
                "rdp-bar--hidden": !barVisible() && status() === "connected",
                "rdp-bar--mini": toolbarMode() === "mini",
              }}
              style={{ left: `${toolbarPos().x}px`, top: `${toolbarPos().y}px` }}
              onMouseMove={wakeBar}
              onMouseEnter={wakeBar}
              onMouseLeave={armBarIdleTimer}
              onPointerMove={moveToolbarDrag}
              onPointerUp={endToolbarDrag}
              onPointerCancel={endToolbarDrag}
            >
              <button
                class="rdp-bar-handle"
                type="button"
                title="Déplacer la barre RDP"
                onPointerDown={beginToolbarDrag}
                onClick={(e) => {
                  e.stopPropagation();
                  if (toolbarSuppressClick) {
                    toolbarSuppressClick = false;
                    return;
                  }
                  if (toolbarMode() === "mini") setToolbarModeValue("expanded");
                }}
              >
                {toolbarMode() === "mini" ? "RDP" : "⋮⋮"}
              </button>
              <Show when={toolbarMode() === "expanded"}>
                <button class="btn btn--sm btn--danger" onClick={disconnect}>
                  Déconnecter
                </button>
                <button
                  class="btn btn--sm"
                  title="Sessions Windows occupant le host — libérer une session bloquante"
                  onClick={() => fetchBusySessions(false)}
                >
                  Sessions
                </button>
                <select
                  class="rdp-bar-select"
                  value={qualityProfile()}
                  title="Qualité RDP"
                  onChange={(e) => setQualityProfileValue(e.currentTarget.value as RdpQualityProfile)}
                >
                  <option value="eco">Éco</option>
                  <option value="standard">Standard</option>
                  <option value="high">Haute</option>
                  <option value="native">Native</option>
                </select>
                <button
                  class="btn btn--sm"
                  classList={{ "btn--primary": cmdKeyMode() === "local" }}
                  onClick={() => setCmdKeyModeValue(cmdKeyMode() === "local" ? "windows" : "local")}
                  title={
                    cmdKeyMode() === "local"
                      ? "Les raccourcis macOS ne sont pas envoyés au RDP"
                      : "Cmd est envoyé comme touche Windows distante"
                  }
                >
                  {cmdKeyMode() === "local" ? "Cmd local" : "Cmd→Windows"}
                </button>
                <button
                  class="btn btn--sm"
                  classList={{ "btn--success": unicodeMode() }}
                  onClick={() => setUnicodeMode((v) => !v)}
                  title={
                    unicodeMode()
                      ? "Unicode ON : caractères envoyés selon votre layout (Mac AZERTY respecté). Désactivez pour PowerShell/terminaux."
                      : "Unicode OFF : scancodes (layout du Windows distant). Activez pour respecter votre layout Mac."
                  }
                >
                  {unicodeMode() ? "ABC ✓" : "ABC"}
                </button>
                <button
                  class="btn btn--sm"
                  onClick={() => setTypeBoxOpen(true)}
                  title="Saisir du texte à envoyer comme un collage (compatible PowerShell)"
                >
                  ⌨ Saisir
                </button>
                <button
                  class="btn btn--sm"
                  classList={{ "btn--primary": isFullscreen() }}
                  onClick={toggleFullscreen}
                  title={isFullscreen() ? "Quitter le plein écran" : "Plein écran RDP"}
                >
                  Plein écran
                </button>
                <button
                  class="btn btn--sm"
                  classList={{ "btn--primary": detached() }}
                  onClick={() => (detached() ? reattachStageFromPopout() : detachLiveStage())}
                  title={detached() ? "Rapatrier la session RDP" : "Détacher la session RDP vivante"}
                >
                  {detached() ? "Rapatrier" : "Détacher"}
                </button>
                <Show when={clipboardAllowsLocalToRemote(clipboardMode())}>
                  <button
                    class="btn btn--sm"
                    onClick={() => void pushClipboardToRemote()}
                    title="Envoyer le clipboard local"
                  >
                    Clip →
                  </button>
                </Show>
                <ClipboardMenu
                  engine={clipboardEngine}
                  open={clipboardMenuOpen()}
                  onToggle={() => setClipboardMenuOpen((v) => !v)}
                  onPushClick={() => void pasteLocalClipboardIntoRemote()}
                  onRequestClick={() => {
                    // Pas de pull explicite en CLIPRDR : le contenu arrive via
                    // remoteClipboardChangedCallback. On ferme juste le menu.
                    setClipboardMenuOpen(false);
                  }}
                  remoteLabel="Windows distant"
                />
                <button
                  class="btn btn--sm"
                  onClick={() => setProfileEditorOpen(true)}
                  title="Profil clavier : Cmd→Ctrl, liste noire, profil personnalisé"
                >
                  ⌨ {activeProfileId() === "custom" ? "Perso" : activeProfileId() === "mac" ? "Cmd" : "Win"}
                </button>
                <button class="btn btn--sm" onClick={() => setToolbarModeValue("mini")} title="Réduire la barre RDP">
                  Réduire
                </button>
                <span
                  class="rdp-status"
                  classList={{
                    "rdp-status--connected": status() === "connected",
                    "rdp-status--connecting": status() === "connecting" || status() === "loading",
                    "rdp-status--error": status() === "error",
                  }}
                >
                  {statusLabel()}
                  {" · "}
                  {qualityProfile() === "eco"
                    ? "éco"
                    : qualityProfile() === "standard"
                      ? "standard"
                      : qualityProfile() === "native"
                        ? "native"
                        : "haute"}
                  {audioEnabled() ? " · audio" : ""}
                  {clipboardMode() !== "none" ? " · clipboard" : ""}
                </span>
                <Show when={error()}>
                  <span class="rdp-status rdp-status--error">{error()}</span>
                </Show>
              </Show>
            </div>
          </Show>
        </div>
      </div>
      <Show when={detached()}>
        <div class="rdp-detached-placeholder">
          <span>Session RDP détachée dans une fenêtre séparée.</span>
          <button class="btn btn--sm" onClick={reattachStageFromPopout}>
            Rapatrier
          </button>
        </div>
      </Show>
      <KeyboardProfileEditor
        open={profileEditorOpen()}
        onClose={() => setProfileEditorOpen(false)}
        activeProfileId={activeProfileId()}
        onProfileChange={onProfileChange}
      />
      {/* Fenêtre de saisie — envoie le texte comme un collage (compatible PowerShell) */}
      <Show when={typeBoxOpen()}>
        <div
          class="rdp-typebox-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setTypeBoxOpen(false);
          }}
        >
          <div class="rdp-typebox-card">
            <div class="rdp-typebox-header">
              <span class="rdp-typebox-title">Saisir du texte à envoyer</span>
              <button class="rdp-typebox-close" onClick={() => setTypeBoxOpen(false)} aria-label="Fermer">
                ✕
              </button>
            </div>
            <p class="rdp-typebox-hint">
              Tapez votre texte (votre layout clavier est respecté), puis cliquez sur « Envoyer ». Le texte sera collé
              sur le Windows distant — compatible PowerShell et tous les terminaux.
            </p>
            <textarea
              ref={(el) => setTimeout(() => el.focus(), 50)}
              class="rdp-typebox-input"
              placeholder="Get-Process | Where-Object {$_.CPU -gt 10}…"
              value={typeBoxText()}
              onInput={(e) => setTypeBoxText(e.currentTarget.value)}
              onKeyDown={(e) => {
                // Ctrl/Cmd+Enter envoie le texte.
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  void sendTypeBoxText();
                }
                // Escape ferme sans envoyer.
                if (e.key === "Escape") {
                  e.preventDefault();
                  setTypeBoxOpen(false);
                }
              }}
            />
            <div class="rdp-typebox-actions">
              <span class="rdp-typebox-shortcut">Ctrl+Entrée pour envoyer</span>
              <button class="btn btn--sm" onClick={() => setTypeBoxOpen(false)}>
                Annuler
              </button>
              <button
                class="btn btn--sm btn--primary"
                disabled={typeBoxSending() || !typeBoxText()}
                onClick={() => void sendTypeBoxText()}
              >
                {typeBoxSending() ? "Envoi…" : "Envoyer"}
              </button>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
};

// Auto-enregistrement dans le registre des panneaux (cf. import.meta.glob).
registerPanel("rdp", RdpPanel);

export default RdpPanel;
